import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { arrayUnion, collection, doc, getDoc, getDocs, increment, limit, orderBy, query, serverTimestamp, setDoc } from 'firebase/firestore'
import { Link } from 'react-router-dom'
import {
  faArrowRight,
  faCamera,
  faChalkboard,
  faChevronDown,
  faCopy,
  faDisplay,
  faEraser,
  faLightbulb,
  faMicrophoneLines,
  faPause,
  faPaperPlane,
  faPen,
  faPhoneSlash,
  faPlay,
  faPlug,
  faRotateRight,
  faStop,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { db } from '../firebase'
import './FireflyComp.css'

const DEFAULT_SERVER_URL = (process.env.REACT_APP_FIREFLY_SERVER_URL || '').replace(/\/$/, '')
const DEFAULT_VOICE = process.env.REACT_APP_FIREFLY_VOICE || 'marin'
const MAX_VISUAL_SNAPSHOT_EDGE = 1280
const VISUAL_SNAPSHOT_QUALITY = 0.72
const USE_DIRECT_FUNCTION_PATHS = /\/firefly$/i.test(DEFAULT_SERVER_URL)
const HEALTH_PATH = USE_DIRECT_FUNCTION_PATHS ? '/health' : '/api/firefly/health'
const RESPOND_PATH = USE_DIRECT_FUNCTION_PATHS ? '/respond' : '/api/firefly/respond'
const TOKEN_PATH = USE_DIRECT_FUNCTION_PATHS ? '/token' : '/api/firefly/token'
const PLM_REFLECT_PATH = USE_DIRECT_FUNCTION_PATHS ? '/plm/reflect' : '/api/firefly/plm/reflect'
const PENDING_PLM_STORAGE_KEY = 'firefly-pending-plm-session'
const MAX_PLM_TOPICS = 8
const MAX_PLM_SIGNALS = 6
const MAX_PLM_WINS = 4
const MAX_TRANSCRIPT_ITEMS_FOR_PLM = 24

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `firefly-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

const buildApiUrl = (path) => `${DEFAULT_SERVER_URL}${path}`
const chartPalette = ['#4484ac', '#44ac96', '#ffb018', '#e53855', '#6b7cff', '#8d6cab']
const richOutputPattern =
  /\b(code|component|function|script|query|sql|diagram|flow|flowchart|chart|graph|plot|equation|formula|math|latex|svg|illustration|draw|analogy|story)\b/i
const conceptTeachingPattern =
  /\b(explain|teach|understand|break down|walk me through|walk through|concept|what is|how does|help me understand)\b/i
const fencedBlockTypes = {
  flow: 'flow',
  'firefly-flow': 'flow',
  chart: 'chart',
  'firefly-chart': 'chart',
  math: 'math',
  'firefly-math': 'math',
  svg: 'svg',
  'firefly-svg': 'svg',
  analogy: 'analogy',
  'firefly-analogy': 'analogy',
  note: 'note',
  'firefly-note': 'note',
}
const fireflyOutputGuide = [
  'The Firefly UI can render code blocks, flow diagrams, charts, formatted math, and SVG illustration cards directly.',
  'The Lesson Board only pins important outputs, so use a dedicated block when you want an important copyable note to appear there.',
  'Use English by default.',
  'Use Swahili only when the student explicitly asks for Swahili.',
  'Do not switch languages unexpectedly within the same lesson.',
  'Never say you cannot display, draw, or render these outputs inside Firefly.',
  'When the student asks for code, a flow diagram, a chart, formatted math, or an SVG illustration, you must answer with a renderable fenced block.',
  'If a concept is abstract, procedural, or likely to confuse the student, you may proactively create a firefly-analogy whiteboard lesson even if they did not explicitly ask for an analogy.',
  'Use proactive analogies selectively, only when they will genuinely improve understanding and memory.',
  'Use normal explanation text outside the blocks.',
  'For code, use standard fenced code blocks with a language tag like ```js or ```python.',
  'For flow diagrams, use ```firefly-flow followed by valid JSON with {"title":"","orientation":"horizontal"|"vertical","nodes":[{"id":"","label":""}],"connections":[{"from":"","to":"","label":"optional"}]}.',
  'For charts, use ```firefly-chart followed by valid JSON with {"title":"","type":"bar"|"line"|"donut","labels":["A","B"],"datasets":[{"label":"Series 1","values":[1,2],"color":"#4484ac"}]}.',
  'For formatted math, use ```firefly-math followed by MathML markup inside one <math>...</math> element whenever possible.',
  'For simple illustrations, use ```firefly-svg followed by a complete simple SVG element with a viewBox and no external assets.',
  'For a funny memorable concept explanation, use ```firefly-analogy followed by valid JSON with {"title":"","concept":"","storyHook":"","voiceoverScript":"","scenes":[{"title":"","visual":"","explanation":""}],"takeaway":""}.',
  'For a short important text the student should copy or remember, use ```firefly-note and place only that key text inside the block.',
].join('\n')

const shouldPreferRichResponse = (prompt) => richOutputPattern.test(prompt) || conceptTeachingPattern.test(prompt)
const shouldRetryRenderableResponse = (prompt) => richOutputPattern.test(prompt)

const buildLearnerMemoryContext = (learnerMemory = null) => {
  if (!learnerMemory) return ''

  const parts = [
    learnerMemory.summary ? `Student profile summary: ${learnerMemory.summary}` : '',
    learnerMemory.preferredTeachingModes?.length
      ? `Best teaching modes so far: ${learnerMemory.preferredTeachingModes.join(', ')}.`
      : '',
    learnerMemory.strengths?.length ? `Likely strengths: ${learnerMemory.strengths.join('; ')}.` : '',
    learnerMemory.struggles?.length ? `Likely struggles: ${learnerMemory.struggles.join('; ')}.` : '',
    learnerMemory.misconceptions?.length
      ? `Watch for these misconceptions: ${learnerMemory.misconceptions.join('; ')}.`
      : '',
    learnerMemory.supportStrategies?.length
      ? `Helpful support strategies: ${learnerMemory.supportStrategies.join('; ')}.`
      : '',
    learnerMemory.diagnoses?.length
      ? `Fine-grained diagnoses: ${learnerMemory.diagnoses
          .map((item) =>
            [
              item.topic ? `topic ${item.topic}` : '',
              item.subtopic ? `subtopic ${item.subtopic}` : '',
              item.misconception ? `misconception ${item.misconception}` : '',
              item.bestIntervention ? `best intervention ${item.bestIntervention}` : '',
            ]
              .filter(Boolean)
              .join(', ')
          )
          .filter(Boolean)
          .join(' | ')}.`
      : '',
    learnerMemory.learningPace ? `Observed pace: ${learnerMemory.learningPace}.` : '',
    learnerMemory.confidenceTrend ? `Observed confidence trend: ${learnerMemory.confidenceTrend}.` : '',
  ].filter(Boolean)

  return parts.join('\n')
}

const buildTutorPrompt = (prompt, learnerMemory = null) =>
  [
    fireflyOutputGuide,
    buildLearnerMemoryContext(learnerMemory),
    'Answer the student request below. If it asks for a visual or code, or if an analogy would genuinely improve understanding, include the proper renderable fenced block in your answer.',
    `Student request:\n${prompt.trim()}`,
  ]
    .filter(Boolean)
    .join('\n\n')

const normalizeList = (items, limit = MAX_PLM_SIGNALS) =>
  [...new Set((items || []).map((item) => String(item || '').trim()).filter(Boolean))].slice(0, limit)

const normalizeDiagnosisList = (items = [], limit = 6) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      topic: String(item?.topic || '').trim(),
      subtopic: String(item?.subtopic || '').trim(),
      misconception: String(item?.misconception || '').trim(),
      evidence: String(item?.evidence || '').trim(),
      bestIntervention: String(item?.bestIntervention || item?.best_intervention || '').trim(),
    }))
    .filter((item) => item.topic || item.subtopic || item.misconception)
    .slice(0, limit)

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value === 'number') return value
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  const parsed = new Date(value).getTime()
  return Number.isFinite(parsed) ? parsed : 0
}

const formatRecommendationDate = (value) => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return 'Recently'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp))
}

const formatRecommendationDateTime = (value) => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return 'an earlier time'
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp))
}

const extractLatestRecommendation = (assignmentProfileDoc, fireflyProfileDoc) => {
  const mapRecommendation = ({ sourceType, sourceLabel, doc }) => {
    const data = doc?.data?.() || {}
    const recommendation = data.profile?.fireflyRecommendation || {}
    const prompt = String(recommendation.inputPrompt || recommendation.detailedPrompt || '').trim()
    if (!prompt) return null

    return {
      sourceType,
      sourceLabel,
      prompt,
      lessonObjective: String(recommendation.lessonObjective || '').trim(),
      studentSummary: String(recommendation.studentSummary || '').trim(),
      urgency: String(recommendation.urgency || '').trim() || 'low',
      updatedAtMs: Math.max(
        normalizeTimestamp(data.lastAnalyzedAt),
        Number(data.lastAnalyzedAtMs) || 0,
        normalizeTimestamp(data.updatedAt),
        Number(data.updatedAtMs) || 0,
        normalizeTimestamp(data.generatedAt),
        Number(data.generatedAtMs) || 0
      ),
    }
  }

  const assignmentRecommendation = mapRecommendation({
    sourceType: 'assignments',
    sourceLabel: 'assignment learning profile',
    doc: assignmentProfileDoc,
  })

  if (assignmentRecommendation) return assignmentRecommendation

  return mapRecommendation({
    sourceType: 'firefly',
    sourceLabel: 'Firefly learning profile',
    doc: fireflyProfileDoc,
  })
}

const formatSessionDuration = (durationSeconds = 0) => {
  const totalSeconds = Math.max(0, Number(durationSeconds) || 0)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  if (minutes <= 0) return `${seconds}s`
  if (seconds === 0) return `${minutes} min`
  return `${minutes} min ${seconds}s`
}

const slugifyTopic = (value = '') =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

const topicMatchers = [
  { label: 'JavaScript', pattern: /\bjavascript\b|\bjs\b/i },
  { label: 'React', pattern: /\breact\b|\bjsx\b/i },
  { label: 'Firebase', pattern: /\bfirebase\b/i },
  { label: 'Firestore', pattern: /\bfirestore\b/i },
  { label: 'Authentication', pattern: /\bauth|authentication|login|sign ?in\b/i },
  { label: 'Node.js', pattern: /\bnode(?:\.js)?\b/i },
  { label: 'APIs', pattern: /\bapi|endpoint|request|response\b/i },
  { label: 'Debugging', pattern: /\bdebug|bug|error|fix|issue|not working\b/i },
  { label: 'For Loops', pattern: /\bfor loop|for-loops?|loop\b/i },
  { label: 'Functions', pattern: /\bfunction|method\b/i },
  { label: 'Arrays', pattern: /\barray|arrays\b/i },
  { label: 'Objects', pattern: /\bobject|objects\b/i },
  { label: 'Promises', pattern: /\bpromise|async|await\b/i },
  { label: 'Screen Review', pattern: /\bscreen|workspace|preview\b/i },
  { label: 'Camera Review', pattern: /\bcamera|photo|object\b/i },
  { label: 'Charts', pattern: /\bchart|graph|plot\b/i },
  { label: 'Flow Diagrams', pattern: /\bdiagram|flow|flowchart|process\b/i },
  { label: 'Math', pattern: /\bmath|equation|formula|quadratic|algebra|integral|matrix\b/i },
]

const extractTopicsFromText = (text = '') => {
  const normalized = String(text || '').replace(/\s+/g, ' ').trim()
  if (!normalized) return []

  const topics = []
  const explicitMatch = normalized.match(
    /\b(?:explain|teach|understand|break down|walk me through|walk through|help me understand|what is|how does|show me)\s+(.+?)(?:[.?!]|$)/i
  )

  if (explicitMatch?.[1]) {
    topics.push(
      explicitMatch[1]
        .replace(/\b(on the whiteboard|on whiteboard|with an analogy|with analogy)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim()
    )
  }

  topicMatchers.forEach(({ label, pattern }) => {
    if (pattern.test(normalized)) {
      topics.push(label)
    }
  })

  return normalizeList(topics, MAX_PLM_TOPICS)
}

const derivePlmInsights = ({
  messages,
  boardEntries,
  voiceUsed,
  screenUsed,
  cameraUsed,
}) => {
  const studentMessages = messages.filter((message) => message.role === 'user')
  const assistantMessages = messages.filter((message) => message.role === 'assistant' && message.source !== 'system')
  const positivePattern = /\b(got it|i get it|i understand|understand now|makes sense|that worked|solved|fixed|clear now|okay i see)\b/i
  const strugglePattern = /\b(confused|stuck|error|bug|issue|not working|don't understand|do not understand|hard|problem|not sure)\b/i

  const topicsCovered = normalizeList(
    [
      ...studentMessages.flatMap((message) => extractTopicsFromText(message.content)),
      ...assistantMessages.flatMap((message) => extractTopicsFromText(message.content)),
      ...boardEntries.map((entry) => entry.title),
    ],
    MAX_PLM_TOPICS
  )

  const recentWins = normalizeList(
    studentMessages
      .filter((message) => positivePattern.test(message.content))
      .map((message) => message.content),
    MAX_PLM_WINS
  )

  const struggles = normalizeList(
    studentMessages.flatMap((message) => {
      if (!strugglePattern.test(message.content)) return []
      const topical = extractTopicsFromText(message.content)
      return topical.length ? topical.map((topic) => `Needs reinforcement in ${topic}`) : ['Needed extra clarification']
    }),
    MAX_PLM_SIGNALS
  )

  const strengths = normalizeList(
    [
      ...(recentWins.length ? recentWins.map(() => 'Showed signs of understanding during the lesson') : []),
      ...(boardEntries.some((entry) => entry.type === 'code') ? ['Worked through code-based explanations'] : []),
      ...(boardEntries.some((entry) => entry.type === 'analogy') ? ['Responded to analogy-based teaching'] : []),
      ...(screenUsed ? ['Used live screen walkthroughs during the lesson'] : []),
    ],
    MAX_PLM_SIGNALS
  )

  const teachingMethodsUsed = normalizeList(
    [
      voiceUsed ? 'voice conversation' : '',
      screenUsed ? 'screen walkthrough' : '',
      cameraUsed ? 'camera guidance' : '',
      boardEntries.some((entry) => entry.type === 'code') ? 'code board' : '',
      boardEntries.some((entry) => entry.type === 'analogy') ? 'analogy board' : '',
      boardEntries.some((entry) => ['flow', 'chart', 'math', 'svg'].includes(entry.type)) ? 'visual whiteboard' : '',
    ],
    MAX_PLM_SIGNALS
  )

  const preferredTeachingModes = normalizeList(
    [
      boardEntries.some((entry) => entry.type === 'analogy') ? 'analogy-first' : '',
      boardEntries.some((entry) => entry.type === 'code') ? 'code-first' : '',
      boardEntries.some((entry) => ['flow', 'chart', 'math', 'svg'].includes(entry.type)) ? 'visual-first' : '',
      screenUsed ? 'screen-guided' : '',
      voiceUsed ? 'conversational' : '',
    ],
    MAX_PLM_SIGNALS
  )

  const summaryParts = [
    topicsCovered.length ? `Covered ${topicsCovered.slice(0, 3).join(', ')}` : 'Covered a focused one-on-one lesson',
    teachingMethodsUsed.length ? `using ${teachingMethodsUsed.slice(0, 3).join(', ')}` : '',
    recentWins.length ? 'and the student showed positive understanding signals.' : struggles.length ? 'and the student needed more reinforcement in a few places.' : '.',
  ]

  return {
    topicsCovered,
    strengths,
    struggles,
    recentWins,
    teachingMethodsUsed,
    preferredTeachingModes,
    summary: summaryParts.filter(Boolean).join(' ').replace(/\s+\./g, '.'),
    boardArtifactTypes: normalizeList(boardEntries.map((entry) => entry.type), MAX_PLM_SIGNALS),
    userTurns: studentMessages.length,
    assistantTurns: assistantMessages.length,
  }
}

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const sanitizeMarkup = (value) =>
  value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/\son[a-z]+=(['"]).*?\1/gi, '')
    .replace(/\son[a-z]+=([^\s>]+)/gi, '')
    .replace(/javascript:/gi, '')

const splitLabel = (label, maxLength = 18) => {
  const words = String(label || '').split(/\s+/).filter(Boolean)
  const lines = []
  let current = ''

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word
    if (candidate.length <= maxLength) {
      current = candidate
      return
    }

    if (current) {
      lines.push(current)
    }
    current = word
  })

  if (current) {
    lines.push(current)
  }

  return lines.length ? lines : [String(label || '')]
}

const hasRenderableBlocks = (content) =>
  parseMessageContent(content).some((block) => ['code', 'flow', 'chart', 'math', 'svg', 'analogy', 'note'].includes(block.type))

const buildRichRetryPrompt = (prompt) =>
  [
    fireflyOutputGuide,
    'Your previous answer was not in a renderable Firefly format.',
    'Re-answer this request using a renderable fenced block and no refusal language.',
    'Keep explanation very short and put the main answer in the block.',
    `Student request:\n${prompt.trim()}`,
  ].join('\n\n')

const detectPreferredRenderableType = (prompt) => {
  const normalizedPrompt = prompt.toLowerCase()

  if (/\b(flow|flowchart|diagram|process)\b/.test(normalizedPrompt)) return 'flow'
  if (/\b(chart|graph|plot)\b/.test(normalizedPrompt)) return 'chart'
  if (/\b(math|equation|formula|latex|quadratic|integral|matrix)\b/.test(normalizedPrompt)) return 'math'
  if (/\b(svg|illustration|draw an illustration|vector)\b/.test(normalizedPrompt)) return 'svg'
  if (/\b(analogy|story|funny example|memorable example)\b/.test(normalizedPrompt)) return 'analogy'
  if (/\b(code|component|function|script|query|sql|react|javascript|python|node)\b/.test(normalizedPrompt)) return 'code'

  return ''
}

const buildStrictRenderableConversionPrompt = (prompt, previousReply) => {
  const preferredType = detectPreferredRenderableType(prompt)
  const blockInstructionMap = {
    flow: 'Return a ```firefly-flow block only. Use valid JSON with nodes and connections.',
    chart: 'Return a ```firefly-chart block only. Use valid JSON with labels and datasets.',
    math: 'Return a ```firefly-math block only. Use MathML inside one <math> element.',
    svg: 'Return a ```firefly-svg block only. Use a complete simple SVG.',
    analogy: 'Return a ```firefly-analogy block only. Use valid JSON with storyHook, voiceoverScript, scenes, and takeaway.',
    code: 'Return a standard fenced code block only, with the most appropriate language tag.',
  }

  return [
    fireflyOutputGuide,
    'Convert the answer below into a renderable Firefly output.',
    'Do not apologize. Do not explain limitations. Do not suggest external tools.',
    blockInstructionMap[preferredType] || 'Return only a renderable fenced block that best matches the request.',
    `Original student request:\n${prompt.trim()}`,
    `Answer to convert:\n${previousReply.trim()}`,
  ].join('\n\n')
}

const buildRealtimeInstructions = ({ studyFocus, codeContext, learnerMemory }) => {
  const sections = [
    'You are Ubunifu Firefly, a warm, technically sharp, instructor-like virtual professor for software students.',
    'Act like a one-on-one classroom instructor, not a generic chatbot.',
    'Teach clearly, ask focused diagnostic questions, use practical examples, and prefer the next smallest helpful step.',
    'Guide the student actively and keep the lesson anchored to their stated objective unless they explicitly ask to change topics.',
    'If the student drifts away from the lesson objective, gently bring them back and explain why the focus matters.',
    'When the student shares code, explain it, find likely issues, and recommend what to try next.',
    'When a code, UI, or debugging issue would be easier to diagnose visually, ask the student to share their screen.',
    'Keep spoken replies concise unless the student asks for a deeper walkthrough.',
  ]

  const learnerContext = buildLearnerMemoryContext(learnerMemory)
  if (learnerContext) {
    sections.push(`Known learner memory:\n${learnerContext}`)
  }

  if (studyFocus.trim()) {
    sections.push(`Current lesson objective. Stay anchored to this unless the student explicitly changes it:\n${studyFocus.trim()}`)
  }

  if (codeContext.trim()) {
    sections.push(`Current project context:\n${codeContext.trim().slice(0, 5000)}`)
  }

  return sections.join('\n\n')
}

const initialMessages = [
  {
    id: createId(),
    role: 'assistant',
    source: 'system',
    content:
      'Start Firefly when you are ready. I can talk with you in realtime, inspect your screen, and look through your camera when you ask me to.',
  },
]

const roleLabel = {
  assistant: 'Firefly',
  user: 'You',
}

const parseMessageContent = (content) => {
  const blocks = []
  const pattern = /```([\w-]*)\n?([\s\S]*?)```/g
  let lastIndex = 0
  let match

  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      const textChunk = content.slice(lastIndex, match.index).trim()
      if (textChunk) {
        blocks.push({
          type: 'text',
          value: textChunk,
        })
      }
    }

    const rawLanguage = (match[1] || '').trim().toLowerCase()
    blocks.push({
      type: fencedBlockTypes[rawLanguage] || 'code',
      language: rawLanguage,
      value: match[2].replace(/\n$/, ''),
    })

    lastIndex = pattern.lastIndex
  }

  if (lastIndex < content.length) {
    const textChunk = content.slice(lastIndex).trim()
    if (textChunk) {
      blocks.push({
        type: 'text',
        value: textChunk,
      })
    }
  }

  if (!blocks.length) {
    blocks.push({
      type: 'text',
      value: content,
    })
  }

  return blocks
}

const renderInlineCode = (text) => {
  const parts = text.split(/(`[^`]+`)/g)

  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <code key={`inline-${index}`} className='fireflyInlineCode'>
          {part.slice(1, -1)}
        </code>
      )
    }

    return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>
  })
}

const createBoardEntries = (messages) => {
  const assistantMessages = messages
    .filter((message) => message.role === 'assistant' && message.source !== 'system')
    .slice()
    .reverse()

  const entries = []

  assistantMessages.forEach((message, messageIndex) => {
    const blocks = parseMessageContent(message.content)

    blocks.forEach((block, blockIndex) => {
      if (!['code', 'flow', 'chart', 'math', 'svg', 'analogy', 'note'].includes(block.type)) {
        return
      }

      entries.push({
        id: `${message.id}-${block.type}-${blockIndex}`,
        type: block.type,
        language: block.language,
        source: message.source,
        title:
          block.type === 'code' ? 'Code Board' :
          block.type === 'flow' ? 'Flow Board' :
          block.type === 'chart' ? 'Chart Board' :
          block.type === 'math' ? 'Math Board' :
          block.type === 'analogy' ? 'Analogy Board' :
          block.type === 'note' ? 'Lesson Note' :
          'Illustration Board',
        content: block.value,
      })
    })
  })

  return entries.slice(0, 8)
}

const boardInkPalette = ['#17332e', '#1d5fa2', '#d43d51']
const analogySceneDurationMs = 4200
const analogyWhiteboardPattern =
  /\b(show|put|use|make|give)\b[\w\s]*\b(analogy|story|funny example)\b[\w\s]*\b(white ?board|board)\b/i

const buildAnalogySceneArtwork = (scene = {}, sceneIndex = 0) => {
  const imageUrl = String(scene?.imageUrl || scene?.imageDataUrl || '').trim()

  if (imageUrl) {
    return <img src={imageUrl} alt={scene.title || `Analogy scene ${sceneIndex + 1}`} className='fireflyAnalogySceneImage' />
  }

  return buildAnalogyVisualScene(scene, sceneIndex)
}

const isAnalogyWhiteboardRequest = (prompt = '') => analogyWhiteboardPattern.test(prompt)

const extractConceptFromAnalogyPrompt = (prompt = '') => {
  const normalized = String(prompt).replace(/\s+/g, ' ').trim()
  if (!normalized) return ''

  const explicitConceptMatch = normalized.match(
    /\b(?:for|about|of|around|regarding|to explain|explaining)\s+(.+?)(?:\s+on(?:\s+the)?\s+white ?board)?[.?!]?$/i
  )
  if (explicitConceptMatch?.[1]) {
    return explicitConceptMatch[1].trim()
  }

  return normalized
    .replace(/\b(show|put|use|make|give|turn|create|build)\b/gi, '')
    .replace(/\b(me|us|an|a|the|this|that|some)\b/gi, '')
    .replace(/\b(analogy|story|funny example|memorable example)\b/gi, '')
    .replace(/\b(on|onto|inside)\s+(?:the\s+)?white ?board\b/gi, '')
    .replace(/\bwhite ?board\b/gi, '')
    .replace(/\b(please|can you|could you|for me)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const buildAnalogyVisualScene = (scene = {}, sceneIndex = 0) => {
  const source = `${scene.title || ''} ${scene.visual || ''} ${scene.explanation || ''}`.toLowerCase()
  const hasGoat = /\bgoat|goats\b/.test(source)
  const hasBus = /\bbus\b/.test(source)
  const hasPerson = /\bperson|teacher|nurse|doctor|guard|student|coder|checker|inspector\b/.test(source)
  const hasHospital = /\bhospital|ward\b/.test(source)
  const hasLoop = /\bloop|for loop|repeat|iterate|check one by one\b/.test(source)
  const hasMath = /\bmath|equation|formula|number\b/.test(source)

  const goatPositions = [72, 152, 232]
  const personX = hasBus ? 304 : 286

  return (
    <svg viewBox='0 0 360 180' className='fireflyAnalogySvg' role='img' aria-label={scene.title || `Analogy scene ${sceneIndex + 1}`}>
      <rect x='14' y='18' width='332' height='148' rx='24' className='fireflyAnalogyBackdrop' />

      {hasHospital ? (
        <g className='fireflyAnalogyHospital'>
          <rect x='26' y='42' width='64' height='64' rx='16' className='fireflyAnalogyBuilding' />
          <rect x='48' y='28' width='20' height='18' rx='6' className='fireflyAnalogySign' />
          <path d='M58 32 v10 M53 37 h10' className='fireflyAnalogySignMark' />
        </g>
      ) : null}

      {hasBus ? (
        <g className='fireflyAnalogyBus fireflyAnalogyFloat'>
          <rect x='86' y='88' width='176' height='44' rx='16' className='fireflyAnalogyVehicle' />
          <rect x='100' y='98' width='28' height='16' rx='5' className='fireflyAnalogyWindow' />
          <rect x='136' y='98' width='28' height='16' rx='5' className='fireflyAnalogyWindow' />
          <rect x='172' y='98' width='28' height='16' rx='5' className='fireflyAnalogyWindow' />
          <rect x='208' y='98' width='28' height='16' rx='5' className='fireflyAnalogyWindow' />
          <circle cx='126' cy='136' r='10' className='fireflyAnalogyWheel' />
          <circle cx='222' cy='136' r='10' className='fireflyAnalogyWheel' />
        </g>
      ) : null}

      {hasGoat ? (
        <g className='fireflyAnalogyGoats'>
          {goatPositions.map((x, goatIndex) => (
            <g
              key={`goat-${goatIndex}`}
              className='fireflyAnalogyGoat'
              style={{ animationDelay: `${goatIndex * 0.12}s` }}
              transform={`translate(${x} 116)`}
            >
              <ellipse cx='0' cy='8' rx='18' ry='14' className='fireflyAnalogyGoatBody' />
              <circle cx='18' cy='0' r='9' className='fireflyAnalogyGoatHead' />
              <path d='M 21 -9 L 26 -16 M 15 -9 L 10 -16' className='fireflyAnalogyGoatHorn' />
              <path d='M -10 18 v16 M 2 18 v16 M 14 18 v16 M -20 18 v16' className='fireflyAnalogyGoatLeg' />
              <circle cx='21' cy='-1' r='1.5' className='fireflyAnalogyGoatEye' />
            </g>
          ))}
        </g>
      ) : null}

      {hasPerson ? (
        <g className='fireflyAnalogyPerson fireflyAnalogyStep' transform={`translate(${personX} 84)`}>
          <circle cx='0' cy='0' r='12' className='fireflyAnalogyPersonHead' />
          <path d='M 0 12 v34 M 0 20 l-18 10 M 0 20 l18 10 M 0 46 l-14 20 M 0 46 l14 20' className='fireflyAnalogyPersonBody' />
          <path d='M -32 -10 L -8 4' className='fireflyAnalogyPointer' />
        </g>
      ) : null}

      {hasLoop ? (
        <g className='fireflyAnalogyLoopPulse'>
          <path d='M 84 62 C 132 34, 228 34, 274 62' className='fireflyAnalogyLoopArrow' />
          <path d='M 274 62 l-14 -2 l7 -11' className='fireflyAnalogyLoopArrowHead' />
        </g>
      ) : null}

      {hasMath ? (
        <g className='fireflyAnalogyMathGroup'>
          <text x='34' y='148' className='fireflyAnalogyMathText'>x + 3 = 7</text>
        </g>
      ) : null}

      {!hasGoat && !hasBus && !hasPerson && !hasHospital && !hasLoop && !hasMath ? (
        <g className='fireflyAnalogyGeneric'>
          <circle cx='92' cy='90' r='30' className='fireflyAnalogyBubble' />
          <circle cx='164' cy='90' r='30' className='fireflyAnalogyBubble fireflyAnalogyFloat' />
          <circle cx='236' cy='90' r='30' className='fireflyAnalogyBubble' />
          <path d='M 122 90 H 134 M 194 90 H 206' className='fireflyAnalogyBubbleLine' />
        </g>
      ) : null}
    </svg>
  )
}

const renderFlowBlock = (value, index) => {
  const definition = safeJsonParse(value)
  if (!definition) return null

  const rawNodes = Array.isArray(definition.nodes) ? definition.nodes : []
  const rawConnections = Array.isArray(definition.connections) ? definition.connections : []
  const orientation = definition.orientation === 'vertical' ? 'vertical' : 'horizontal'
  const nodes =
    rawNodes.length > 0
      ? rawNodes.map((node, nodeIndex) => ({
          id: String(node.id || `node-${nodeIndex}`),
          label: String(node.label || node.id || `Step ${nodeIndex + 1}`),
        }))
      : []

  if (!nodes.length) return null

  const orderMap = new Map(nodes.map((node, nodeIndex) => [node.id, nodeIndex]))
  const incomingCount = new Map(nodes.map((node) => [node.id, 0]))
  const levels = new Map(nodes.map((node) => [node.id, 0]))

  rawConnections.forEach((connection) => {
    const toId = String(connection.to || '')
    if (incomingCount.has(toId)) {
      incomingCount.set(toId, (incomingCount.get(toId) || 0) + 1)
    }
  })

  const queue = nodes
    .filter((node) => (incomingCount.get(node.id) || 0) === 0)
    .map((node) => node.id)

  if (!queue.length) {
    queue.push(nodes[0].id)
  }

  while (queue.length) {
    const currentId = queue.shift()
    const currentLevel = levels.get(currentId) || 0

    rawConnections
      .filter((connection) => String(connection.from || '') === currentId)
      .forEach((connection) => {
        const nextId = String(connection.to || '')
        if (!levels.has(nextId)) return
        const nextLevel = Math.max(levels.get(nextId) || 0, currentLevel + 1)
        if (nextLevel !== levels.get(nextId)) {
          levels.set(nextId, nextLevel)
          queue.push(nextId)
        }
      })
  }

  const groupedNodes = new Map()
  nodes.forEach((node) => {
    const level = levels.get(node.id) || 0
    const current = groupedNodes.get(level) || []
    current.push(node)
    groupedNodes.set(level, current)
  })

  const orderedLevels = [...groupedNodes.keys()].sort((first, second) => first - second)
  orderedLevels.forEach((level) => {
    groupedNodes.get(level).sort((first, second) => (orderMap.get(first.id) || 0) - (orderMap.get(second.id) || 0))
  })

  const nodeWidth = 158
  const nodeHeight = 58
  const laneGap = 54
  const levelGap = 96
  const padding = 28
  const coordinates = new Map()
  const maxLaneCount = Math.max(...orderedLevels.map((level) => groupedNodes.get(level).length))

  orderedLevels.forEach((level) => {
    const nodesAtLevel = groupedNodes.get(level)
    nodesAtLevel.forEach((node, laneIndex) => {
      const laneOffset = maxLaneCount > nodesAtLevel.length ? (maxLaneCount - nodesAtLevel.length) / 2 : 0
      const lanePosition = laneIndex + laneOffset

      const x =
        orientation === 'horizontal'
          ? padding + level * (nodeWidth + levelGap)
          : padding + lanePosition * (nodeWidth + laneGap)
      const y =
        orientation === 'horizontal'
          ? padding + lanePosition * (nodeHeight + laneGap)
          : padding + level * (nodeHeight + levelGap)

      coordinates.set(node.id, { x, y })
    })
  })

  const width =
    orientation === 'horizontal'
      ? padding * 2 + orderedLevels.length * nodeWidth + Math.max(orderedLevels.length - 1, 0) * levelGap
      : padding * 2 + maxLaneCount * nodeWidth + Math.max(maxLaneCount - 1, 0) * laneGap
  const height =
    orientation === 'horizontal'
      ? padding * 2 + maxLaneCount * nodeHeight + Math.max(maxLaneCount - 1, 0) * laneGap
      : padding * 2 + orderedLevels.length * nodeHeight + Math.max(orderedLevels.length - 1, 0) * levelGap

  return (
    <div key={`flow-${index}`} className='fireflyRichCard fireflyFlowCard'>
      {definition.title ? <div className='fireflyRichCardHeader'>{definition.title}</div> : null}
      <div className='fireflyRichCardBody'>
        <svg viewBox={`0 0 ${width} ${height}`} className='fireflyDiagramSvg' role='img' aria-label={definition.title || 'Flow diagram'}>
          <defs>
            <marker id={`firefly-arrow-${index}`} markerWidth='10' markerHeight='10' refX='8' refY='5' orient='auto'>
              <path d='M 0 0 L 10 5 L 0 10 z' fill='#7a8ea2' />
            </marker>
          </defs>

          {rawConnections.map((connection, connectionIndex) => {
            const fromId = String(connection.from || '')
            const toId = String(connection.to || '')
            const start = coordinates.get(fromId)
            const end = coordinates.get(toId)
            if (!start || !end) return null

            const startX = orientation === 'horizontal' ? start.x + nodeWidth : start.x + nodeWidth / 2
            const startY = orientation === 'horizontal' ? start.y + nodeHeight / 2 : start.y + nodeHeight
            const endX = orientation === 'horizontal' ? end.x : end.x + nodeWidth / 2
            const endY = orientation === 'horizontal' ? end.y + nodeHeight / 2 : end.y
            const curveMidX = startX + (endX - startX) / 2
            const curveMidY = startY + (endY - startY) / 2
            const path =
              orientation === 'horizontal'
                ? `M ${startX} ${startY} C ${curveMidX} ${startY}, ${curveMidX} ${endY}, ${endX} ${endY}`
                : `M ${startX} ${startY} C ${startX} ${curveMidY}, ${endX} ${curveMidY}, ${endX} ${endY}`

            return (
              <g key={`connection-${connectionIndex}`}>
                <path
                  d={path}
                  fill='none'
                  stroke='#7a8ea2'
                  strokeWidth='2.4'
                  strokeLinecap='round'
                  markerEnd={`url(#firefly-arrow-${index})`}
                />
                {connection.label ? (
                  <text
                    x={(startX + endX) / 2}
                    y={(startY + endY) / 2 - 8}
                    textAnchor='middle'
                    className='fireflyDiagramEdgeLabel'
                  >
                    {String(connection.label)}
                  </text>
                ) : null}
              </g>
            )
          })}

          {nodes.map((node) => {
            const point = coordinates.get(node.id)
            if (!point) return null

            return (
              <g key={node.id} transform={`translate(${point.x}, ${point.y})`}>
                <rect width={nodeWidth} height={nodeHeight} rx='16' className='fireflyDiagramNode' />
                {splitLabel(node.label).map((line, lineIndex) => (
                  <text
                    key={`${node.id}-${lineIndex}`}
                    x={nodeWidth / 2}
                    y={24 + lineIndex * 16}
                    textAnchor='middle'
                    className='fireflyDiagramNodeLabel'
                  >
                    {line}
                  </text>
                ))}
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

const renderChartBlock = (value, index) => {
  const definition = safeJsonParse(value)
  if (!definition || !Array.isArray(definition.labels) || !Array.isArray(definition.datasets)) return null

  const labels = definition.labels.map((label) => String(label))
  const datasets = definition.datasets.map((dataset, datasetIndex) => ({
    label: String(dataset.label || `Series ${datasetIndex + 1}`),
    values: Array.isArray(dataset.values) ? dataset.values.map((item) => Number(item) || 0) : [],
    color: dataset.color || chartPalette[datasetIndex % chartPalette.length],
  }))
  const type = ['line', 'donut'].includes(definition.type) ? definition.type : 'bar'
  const width = 620
  const height = 320
  const chartHeight = 210
  const chartLeft = 54
  const chartBottom = 244
  const chartWidth = 520
  const allValues = datasets.flatMap((dataset) => dataset.values)
  const maxValue = Math.max(1, ...allValues)

  const renderBarChart = () => {
    const groups = labels.length || 1
    const groupWidth = chartWidth / groups
    const barWidth = Math.max(18, (groupWidth - 18) / Math.max(datasets.length, 1))

    return (
      <>
        {[0, 1, 2, 3, 4].map((tick) => {
          const value = (maxValue / 4) * (4 - tick)
          const y = chartBottom - (chartHeight * (value / maxValue))
          return (
            <g key={`tick-${tick}`}>
              <line x1={chartLeft} y1={y} x2={chartLeft + chartWidth} y2={y} className='fireflyChartGrid' />
              <text x={chartLeft - 10} y={y + 4} textAnchor='end' className='fireflyChartAxisLabel'>
                {Math.round(value * 10) / 10}
              </text>
            </g>
          )
        })}
        {labels.map((label, labelIndex) => {
          const groupX = chartLeft + labelIndex * groupWidth
          return (
            <g key={`group-${label}`}>
              {datasets.map((dataset, datasetIndex) => {
                const value = dataset.values[labelIndex] || 0
                const barHeight = chartHeight * (value / maxValue)
                const x = groupX + 10 + datasetIndex * barWidth
                const y = chartBottom - barHeight
                return (
                  <rect
                    key={`${dataset.label}-${label}`}
                    x={x}
                    y={y}
                    width={barWidth - 8}
                    height={barHeight}
                    rx='8'
                    fill={dataset.color}
                    opacity='0.92'
                  />
                )
              })}
              <text x={groupX + groupWidth / 2} y={chartBottom + 22} textAnchor='middle' className='fireflyChartAxisLabel'>
                {label}
              </text>
            </g>
          )
        })}
      </>
    )
  }

  const renderLineChart = () => {
    const step = labels.length > 1 ? chartWidth / (labels.length - 1) : chartWidth / 2

    return (
      <>
        {[0, 1, 2, 3, 4].map((tick) => {
          const value = (maxValue / 4) * (4 - tick)
          const y = chartBottom - (chartHeight * (value / maxValue))
          return (
            <g key={`line-tick-${tick}`}>
              <line x1={chartLeft} y1={y} x2={chartLeft + chartWidth} y2={y} className='fireflyChartGrid' />
              <text x={chartLeft - 10} y={y + 4} textAnchor='end' className='fireflyChartAxisLabel'>
                {Math.round(value * 10) / 10}
              </text>
            </g>
          )
        })}
        {datasets.map((dataset) => {
          const points = dataset.values.map((value, pointIndex) => {
            const x = chartLeft + pointIndex * step
            const y = chartBottom - chartHeight * ((value || 0) / maxValue)
            return { x, y, value }
          })
          const polyline = points.map((point) => `${point.x},${point.y}`).join(' ')

          return (
            <g key={dataset.label}>
              <polyline fill='none' stroke={dataset.color} strokeWidth='3' points={polyline} />
              {points.map((point, pointIndex) => (
                <circle key={`${dataset.label}-${pointIndex}`} cx={point.x} cy={point.y} r='4.5' fill={dataset.color} />
              ))}
            </g>
          )
        })}
        {labels.map((label, labelIndex) => (
          <text
            key={`line-label-${label}`}
            x={chartLeft + labelIndex * step}
            y={chartBottom + 22}
            textAnchor='middle'
            className='fireflyChartAxisLabel'
          >
            {label}
          </text>
        ))}
      </>
    )
  }

  const renderDonutChart = () => {
    const values = datasets[0]?.values || []
    const total = Math.max(values.reduce((sum, value) => sum + value, 0), 1)
    const centerX = 220
    const centerY = 156
    const radius = 80
    const strokeWidth = 42
    let currentAngle = -90

    const polarToCartesian = (angle) => {
      const radians = (angle * Math.PI) / 180
      return {
        x: centerX + radius * Math.cos(radians),
        y: centerY + radius * Math.sin(radians),
      }
    }

    return (
      <>
        {values.map((value, segmentIndex) => {
          const sweep = (value / total) * 360
          const start = polarToCartesian(currentAngle)
          const end = polarToCartesian(currentAngle + sweep)
          const largeArc = sweep > 180 ? 1 : 0
          const path = `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`
          const stroke = datasets[0]?.color || chartPalette[segmentIndex % chartPalette.length]
          currentAngle += sweep

          return <path key={`segment-${segmentIndex}`} d={path} fill='none' stroke={stroke} strokeWidth={strokeWidth} />
        })}

        <text x={centerX} y={148} textAnchor='middle' className='fireflyChartDonutValue'>
          {Math.round(total * 10) / 10}
        </text>
        <text x={centerX} y={170} textAnchor='middle' className='fireflyChartDonutLabel'>
          total
        </text>

        <g transform='translate(380 82)'>
          {labels.map((label, labelIndex) => {
            const color = datasets[0]?.color && labels.length === 1 ? datasets[0].color : chartPalette[labelIndex % chartPalette.length]
            return (
              <g key={`legend-${labelIndex}`} transform={`translate(0 ${labelIndex * 28})`}>
                <rect width='12' height='12' rx='4' fill={color} />
                <text x='20' y='10' className='fireflyChartLegend'>
                  {label}: {values[labelIndex] || 0}
                </text>
              </g>
            )
          })}
        </g>
      </>
    )
  }

  return (
    <div key={`chart-${index}`} className='fireflyRichCard fireflyChartCard'>
      {definition.title ? <div className='fireflyRichCardHeader'>{definition.title}</div> : null}
      <div className='fireflyRichCardBody'>
        <svg viewBox={`0 0 ${width} ${height}`} className='fireflyChartSvg' role='img' aria-label={definition.title || 'Chart'}>
          {type !== 'donut' ? (
            <>
              <line x1={chartLeft} y1={chartBottom} x2={chartLeft + chartWidth} y2={chartBottom} className='fireflyChartAxis' />
              <line x1={chartLeft} y1={chartBottom} x2={chartLeft} y2={34} className='fireflyChartAxis' />
            </>
          ) : null}
          {type === 'bar' ? renderBarChart() : null}
          {type === 'line' ? renderLineChart() : null}
          {type === 'donut' ? renderDonutChart() : null}
        </svg>
      </div>
    </div>
  )
}

const renderMathBlock = (value, index) => {
  const sanitized = sanitizeMarkup(value.trim())
  const isMathMarkup = /^<math[\s>]/i.test(sanitized)

  return (
    <div key={`math-${index}`} className='fireflyRichCard fireflyMathCard'>
      <div className='fireflyRichCardHeader'>Formatted Equation</div>
      <div className='fireflyRichCardBody'>
        {isMathMarkup ? (
          <div className='fireflyMathMarkup' dangerouslySetInnerHTML={{ __html: sanitized }} />
        ) : (
          <pre className='fireflyMathFallback'>
            <code>{value}</code>
          </pre>
        )}
      </div>
    </div>
  )
}

const renderSvgBlock = (value, index) => {
  const sanitized = sanitizeMarkup(value.trim())
  if (!/^<svg[\s>]/i.test(sanitized)) return null

  return (
    <div key={`svg-${index}`} className='fireflyRichCard fireflySvgCard'>
      <div className='fireflyRichCardHeader'>Illustration</div>
      <div className='fireflyRichCardBody'>
        <div className='fireflySvgFrame' dangerouslySetInnerHTML={{ __html: sanitized }} />
      </div>
    </div>
  )
}

const FireflyAnalogyPlayer = ({ definition, analogySource }) => {
  const scenes = Array.isArray(definition?.scenes) ? definition.scenes.slice(0, 4) : []
  const [activeSceneIndex, setActiveSceneIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(scenes.length > 1)

  useEffect(() => {
    setActiveSceneIndex(0)
    setIsPlaying(scenes.length > 1)
  }, [analogySource, scenes.length])

  useEffect(() => {
    if (!isPlaying || scenes.length <= 1) return undefined

    const timer = window.setInterval(() => {
      setActiveSceneIndex((current) => {
        if (current >= scenes.length - 1) {
          window.clearInterval(timer)
          setIsPlaying(false)
          return current
        }

        return current + 1
      })
    }, analogySceneDurationMs)

    return () => {
      window.clearInterval(timer)
    }
  }, [isPlaying, scenes.length])

  const currentScene = scenes[activeSceneIndex] || null
  const progressWidth = scenes.length ? `${((activeSceneIndex + 1) / scenes.length) * 100}%` : '0%'
  const currentCaption =
    currentScene?.explanation?.trim() ||
    currentScene?.visual?.trim() ||
    definition?.storyHook?.trim() ||
    definition?.takeaway?.trim() ||
    ''

  const startPlaybackFromCurrentPoint = ({ forceRestart = false } = {}) => {
    if (forceRestart || activeSceneIndex >= scenes.length - 1) {
      setActiveSceneIndex(0)
    }

    setIsPlaying(scenes.length > 1)
  }

  return (
    <div className='fireflyRichCard fireflyAnalogyCard'>
      <div className='fireflyRichCardHeader fireflyAnalogyHeader'>
        <div>
          <span>Analogy Lesson</span>
          {definition.title ? <strong>{definition.title}</strong> : null}
        </div>
      </div>

      <div className='fireflyRichCardBody fireflyAnalogyPlayerBody'>
        {definition.storyHook ? <p className='fireflyAnalogyHook'>{definition.storyHook}</p> : null}

        {currentScene ? (
          <div className='fireflyAnalogyPlayerShell'>
            <div className='fireflyAnalogyPlayerFrame'>
              <div className='fireflyAnalogyPlayerScreen'>
                <div className='fireflyAnalogyPlayerBadge'>Whiteboard Story</div>
                <div className='fireflyAnalogyPlayerStage'>
                  <div
                    key={`analogy-scene-${activeSceneIndex}-${currentScene.title || definition.title || 'scene'}`}
                    className='fireflyAnalogyPlayerArtwork'
                  >
                    {buildAnalogySceneArtwork(currentScene, activeSceneIndex)}
                  </div>
                  <div className='fireflyAnalogyPlayerOverlay'>
                    <span>Explainer Video</span>
                    <strong>{definition.title || currentScene.title || 'Analogy lesson'}</strong>
                    {currentCaption ? <p>{currentCaption}</p> : null}
                  </div>
                </div>

                <div className='fireflyAnalogyPlayerControls'>
                  <div className='fireflyAnalogyProgressTrack'>
                    <span className='fireflyAnalogyProgressValue' style={{ width: progressWidth }} />
                  </div>

                  <div className='fireflyAnalogyControlRow'>
                    <div className='fireflyAnalogyButtons'>
                      <button
                        type='button'
                        className='fireflyBoardAction fireflyAnalogyTransportBtn'
                        onClick={() => {
                          if (isPlaying) {
                            setIsPlaying(false)
                            return
                          }

                          startPlaybackFromCurrentPoint()
                        }}
                        disabled={scenes.length <= 1}
                      >
                        <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                        {isPlaying ? 'Pause' : 'Play'}
                      </button>
                      <button
                        type='button'
                        className='fireflyBoardAction fireflyAnalogyTransportBtn'
                        onClick={() => startPlaybackFromCurrentPoint({ forceRestart: true })}
                      >
                        <FontAwesomeIcon icon={faRotateRight} />
                        Restart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {definition.takeaway ? (
          <div className='fireflyAnalogyTakeaway'>
            <span>Why it sticks</span>
            <p>{definition.takeaway}</p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const renderAnalogyBlock = (value, index, options = {}) => {
  const definition = safeJsonParse(value)
  if (!definition) return null

  return (
    <FireflyAnalogyPlayer
      key={`analogy-${index}`}
      definition={definition}
      analogySource={value}
    />
  )
}

const renderRenderableBlock = (block, key, options = {}) => {
  const showFallbackLabel = options.showFallbackLabel ?? true

  if (block.type === 'note') {
    return (
      <div key={`note-${key}`} className='fireflyBoardNote'>
        <p>{renderInlineCode(block.content ?? block.value)}</p>
      </div>
    )
  }

  if (block.type === 'flow') {
    return renderFlowBlock(block.content ?? block.value, key) || (
      <div key={`flow-fallback-${key}`} className='fireflyCodeBlock'>
        {showFallbackLabel ? <span className='fireflyCodeLabel'>{block.language || 'firefly-flow'}</span> : null}
        <pre>
          <code>{block.content ?? block.value}</code>
        </pre>
      </div>
    )
  }

  if (block.type === 'chart') {
    return renderChartBlock(block.content ?? block.value, key) || (
      <div key={`chart-fallback-${key}`} className='fireflyCodeBlock'>
        {showFallbackLabel ? <span className='fireflyCodeLabel'>{block.language || 'firefly-chart'}</span> : null}
        <pre>
          <code>{block.content ?? block.value}</code>
        </pre>
      </div>
    )
  }

  if (block.type === 'math') {
    return renderMathBlock(block.content ?? block.value, key)
  }

  if (block.type === 'svg') {
    return renderSvgBlock(block.content ?? block.value, key) || (
      <div key={`svg-fallback-${key}`} className='fireflyCodeBlock'>
        {showFallbackLabel ? <span className='fireflyCodeLabel'>{block.language || 'firefly-svg'}</span> : null}
        <pre>
          <code>{block.content ?? block.value}</code>
        </pre>
      </div>
    )
  }

  if (block.type === 'analogy') {
    return renderAnalogyBlock(block.content ?? block.value, key, options) || (
      <div key={`analogy-fallback-${key}`} className='fireflyCodeBlock'>
        {showFallbackLabel ? <span className='fireflyCodeLabel'>{block.language || 'firefly-analogy'}</span> : null}
        <pre>
          <code>{block.content ?? block.value}</code>
        </pre>
      </div>
    )
  }

  if (block.type === 'code') {
    return (
      <div key={`code-${key}`} className='fireflyCodeBlock'>
        {block.language ? <span className='fireflyCodeLabel'>{block.language}</span> : null}
        <pre>
          <code>{block.content ?? block.value}</code>
        </pre>
      </div>
    )
  }

  return (
    <p key={`text-${key}`} className='fireflyMessageParagraph'>
      {renderInlineCode(block.content ?? block.value)}
    </p>
  )
}

function FireflyComp() {
  const auth = useMemo(() => getAuth(), [])
  const [healthStatus, setHealthStatus] = useState('checking')
  const [tutorState, setTutorState] = useState('idle')
  const [voiceStatus, setVoiceStatus] = useState('idle')
  const [screenStatus, setScreenStatus] = useState('idle')
  const [cameraStatus, setCameraStatus] = useState('idle')
  const [messages, setMessages] = useState(initialMessages)
  const [chatInput, setChatInput] = useState('')
  const [studyFocus, setStudyFocus] = useState('')
  const [recommendedStudyFocus, setRecommendedStudyFocus] = useState('')
  const [recommendedFocusMeta, setRecommendedFocusMeta] = useState(null)
  const [previousSessionMeta, setPreviousSessionMeta] = useState(null)
  const [codeContext] = useState('')
  const [selectedVoice] = useState(DEFAULT_VOICE)
  const [systemMessage, setSystemMessage] = useState('')
  const [pageError, setPageError] = useState('')
  const [voiceError, setVoiceError] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [isAnalyzingVisual, setIsAnalyzingVisual] = useState(false)
  const [isGeneratingAnalogy, setIsGeneratingAnalogy] = useState(false)
  const [includeVisualInNextTurn, setIncludeVisualInNextTurn] = useState(false)
  const speakReplies = false
  const [activeVisualSource, setActiveVisualSource] = useState('screen')
  const [isTranscriptVisible, setIsTranscriptVisible] = useState(false)
  const [boardPendingState, setBoardPendingState] = useState(null)
  const [boardTool, setBoardTool] = useState('view')
  const [boardInkColor, setBoardInkColor] = useState(boardInkPalette[0])
  const [boardStrokeWidth, setBoardStrokeWidth] = useState(3)
  const [isBoardInkVisible, setIsBoardInkVisible] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [learnerMemory, setLearnerMemory] = useState(null)
  const [lastSessionReport, setLastSessionReport] = useState(null)
  const [isGeneratingSessionReport, setIsGeneratingSessionReport] = useState(false)

  const screenPreviewRef = useRef(null)
  const screenCanvasRef = useRef(null)
  const screenStreamRef = useRef(null)
  const cameraPreviewRef = useRef(null)
  const cameraCanvasRef = useRef(null)
  const cameraStreamRef = useRef(null)
  const boardStageRef = useRef(null)
  const boardCanvasRef = useRef(null)
  const boardDrawingStateRef = useRef({
    isDrawing: false,
    pointerId: null,
  })
  const messagesContainerRef = useRef(null)
  const messagesEndRef = useRef(null)
  const realtimePeerRef = useRef(null)
  const realtimeChannelRef = useRef(null)
  const realtimeAudioRef = useRef(null)
  const realtimeLocalStreamRef = useRef(null)
  const seenVoiceUserItemsRef = useRef(new Set())
  const seenVoiceAssistantItemsRef = useRef(new Set())
  const plmSessionIdRef = useRef('')
  const plmSessionStartedAtRef = useRef(0)
  const isFlushingPendingPlmRef = useRef(false)
  const latestPlmSnapshotRef = useRef(null)
  const latestVisualStateRef = useRef({
    activeVisualSource: 'screen',
    screenStatus: 'idle',
    cameraStatus: 'idle',
    studyFocus: '',
    codeContext: '',
  })

  const realtimeInstructions = useMemo(
    () => buildRealtimeInstructions({ studyFocus, codeContext, learnerMemory }),
    [studyFocus, codeContext, learnerMemory]
  )
  const boardEntries = useMemo(() => createBoardEntries(messages), [messages])
  const latestBoardEntry = boardEntries[0] || null
  const pinnedBoardEntries = latestBoardEntry ? boardEntries.slice(1) : []

  const buildPlmSessionSnapshot = useCallback(({
    completionStatus = 'completed',
    endedAtMs = Date.now(),
  } = {}) => {
    const user = auth.currentUser || currentUser
    const sessionId = plmSessionIdRef.current
    const startedAtMs = plmSessionStartedAtRef.current

    if (!user?.uid || !sessionId || !startedAtMs) return null

    const voiceUsed = voiceStatus === 'live' || messages.some((message) => message.source === 'voice')
    const screenUsed = screenStatus === 'live' || messages.some((message) => message.source === 'screen')
    const cameraUsed = cameraStatus === 'live' || messages.some((message) => message.source === 'camera')
    const insights = derivePlmInsights({
      messages,
      boardEntries,
      voiceUsed,
      screenUsed,
      cameraUsed,
    })

    return {
      version: 1,
      sessionId,
      studentId: user.uid,
      studentName: user.displayName || user.email || 'Student',
      studentEmail: user.email || '',
      sessionFocus: studyFocus.trim(),
      startedAtMs,
      endedAtMs,
      durationSeconds: Math.max(0, Math.round((endedAtMs - startedAtMs) / 1000)),
      completionStatus,
      diagnoses: [],
      ...insights,
    }
  }, [auth, boardEntries, cameraStatus, currentUser, messages, screenStatus, studyFocus, voiceStatus])

  const loadLearnerMemory = useCallback(async (studentId) => {
    if (!studentId) {
      setLearnerMemory(null)
      return null
    }

    try {
      const snapshot = await getDoc(doc(db, 'plm', studentId))
      if (!snapshot.exists()) {
        setLearnerMemory(null)
        return null
      }

      const data = snapshot.data() || {}
      const nextMemory = {
        summary: String(data.lastSessionSummary || data.summary || '').trim(),
        strengths: normalizeList(data.strengths, MAX_PLM_SIGNALS),
        struggles: normalizeList(data.struggles, MAX_PLM_SIGNALS),
        misconceptions: normalizeList(data.misconceptions, MAX_PLM_SIGNALS),
        diagnoses: normalizeDiagnosisList(data.diagnoses),
        supportStrategies: normalizeList(data.supportStrategies, MAX_PLM_SIGNALS),
        preferredTeachingModes: normalizeList(
          data.preferredTeachingModes || data.preferredModesSeen,
          MAX_PLM_SIGNALS
        ),
        recentWins: normalizeList(data.recentWins, MAX_PLM_WINS),
        learningPace: String(data.learningPace || '').trim(),
        confidenceTrend: String(data.confidenceTrend || '').trim(),
        recommendedNextSteps: normalizeList(data.recommendedNextSteps, MAX_PLM_SIGNALS),
      }

      setLearnerMemory(nextMemory)
      return nextMemory
    } catch (error) {
      return null
    }
  }, [])

  const loadLatestProfileRecommendation = useCallback(async (studentId) => {
    if (!studentId) {
      setRecommendedStudyFocus('')
      setRecommendedFocusMeta(null)
      return null
    }

    try {
      const [assignmentProfileDoc, fireflyProfileDoc] = await Promise.all([
        getDoc(doc(db, 'studentAssignmentLearningProfiles', studentId)),
        getDoc(doc(db, 'studentFireflyLearningProfiles', studentId)),
      ])

      const recommendation = extractLatestRecommendation(assignmentProfileDoc, fireflyProfileDoc)
      setRecommendedStudyFocus(recommendation?.prompt || '')
      setRecommendedFocusMeta(recommendation)
      setStudyFocus(recommendation?.prompt || '')
      return recommendation
    } catch (error) {
      setRecommendedStudyFocus('')
      setRecommendedFocusMeta(null)
      return null
    }
  }, [])

  const loadPreviousSessionMeta = useCallback(async (studentId) => {
    if (!studentId) {
      setPreviousSessionMeta(null)
      return null
    }

    try {
      const latestSessionQuery = query(
        collection(db, 'plm', studentId, 'sessions'),
        orderBy('endedAtMs', 'desc'),
        limit(1)
      )
      const snapshot = await getDocs(latestSessionQuery)
      const latestDoc = snapshot.docs[0]
      if (!latestDoc) {
        setPreviousSessionMeta(null)
        return null
      }

      const data = latestDoc.data() || {}
      const nextMeta = {
        startedAtMs: Number(data.startedAtMs) || 0,
        endedAtMs: Number(data.endedAtMs) || 0,
        sessionFocus: String(data.sessionFocus || '').trim(),
      }

      setPreviousSessionMeta(nextMeta)
      return nextMeta
    } catch (error) {
      setPreviousSessionMeta(null)
      return null
    }
  }, [])

  const buildPlmTranscript = useCallback(() => {
    return messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .slice(-MAX_TRANSCRIPT_ITEMS_FOR_PLM)
      .map((message) => ({
        role: message.role,
        source: message.source,
        content: String(message.content || '').slice(0, 2200),
      }))
  }, [messages])

  const requestPlmReflection = useCallback(async (sessionSnapshot) => {
    if (!sessionSnapshot?.studentId) return null

    try {
      const response = await fetch(buildApiUrl(PLM_REFLECT_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          learnerMemory,
          sessionSnapshot,
          transcript: buildPlmTranscript(),
          boardEntries: boardEntries.slice(0, 6).map((entry) => ({
            type: entry.type,
            title: entry.title,
            content: String(entry.content || '').slice(0, 1800),
          })),
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data?.error || 'Firefly could not reflect on the learner profile right now.')
      }

      return data?.reflection || null
    } catch (error) {
      return null
    }
  }, [boardEntries, buildPlmTranscript, learnerMemory])

  const mergeReflectionIntoSnapshot = useCallback((snapshot, reflection) => {
    if (!snapshot || !reflection) return snapshot

    return {
      ...snapshot,
      summary: String(reflection.summary || snapshot.summary || '').trim(),
      strengths: normalizeList([...(snapshot.strengths || []), ...(reflection.strengths || [])], MAX_PLM_SIGNALS),
      struggles: normalizeList([...(snapshot.struggles || []), ...(reflection.struggles || [])], MAX_PLM_SIGNALS),
      misconceptions: normalizeList(reflection.misconceptions || [], MAX_PLM_SIGNALS),
      supportStrategies: normalizeList(reflection.supportStrategies || [], MAX_PLM_SIGNALS),
      preferredTeachingModes: normalizeList(
        [...(snapshot.preferredTeachingModes || []), ...(reflection.preferredTeachingModes || [])],
        MAX_PLM_SIGNALS
      ),
      recommendedNextSteps: normalizeList(reflection.recommendedNextSteps || [], MAX_PLM_SIGNALS),
      learningPace: String(reflection.learningPace || '').trim(),
      confidenceTrend: String(reflection.confidenceTrend || '').trim(),
      reflectionConfidence: String(reflection.reflectionConfidence || '').trim(),
      topicsCovered: normalizeList(
        [...(snapshot.topicsCovered || []), ...(reflection.topicsCovered || [])],
        MAX_PLM_TOPICS
      ),
      diagnoses: normalizeDiagnosisList(reflection.diagnoses || []),
    }
  }, [])

  const persistPlmSnapshot = useCallback(async (snapshot) => {
    if (!snapshot?.studentId || !snapshot?.sessionId) return

    const learnerRef = doc(db, 'plm', snapshot.studentId)
    const sessionRef = doc(db, 'plm', snapshot.studentId, 'sessions', snapshot.sessionId)
    const topicWrites = snapshot.topicsCovered.map((topic) => {
      const topicId = slugifyTopic(topic)
      if (!topicId) return null
      const relatedDiagnosis =
        (snapshot.diagnoses || []).find(
          (item) =>
            item.topic?.toLowerCase() === topic.toLowerCase() ||
            item.subtopic?.toLowerCase() === topic.toLowerCase()
        ) || null

      return setDoc(
        doc(db, 'plm', snapshot.studentId, 'topics', topicId),
        {
          topic,
          lastSeenAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          source: 'firefly',
          timesSeen: increment(1),
          needsReview: snapshot.struggles.some((item) => item.toLowerCase().includes(topic.toLowerCase())),
          preferredTeachingModes: snapshot.preferredTeachingModes,
          lastDiagnosis: relatedDiagnosis,
        },
        { merge: true }
      )
    }).filter(Boolean)

    await Promise.all([
      setDoc(
        learnerRef,
        {
          studentId: snapshot.studentId,
          studentName: snapshot.studentName,
          studentEmail: snapshot.studentEmail,
          source: 'firefly',
          activeSessionId: snapshot.completionStatus === 'started' ? snapshot.sessionId : null,
          lastSessionStatus: snapshot.completionStatus,
          ...(snapshot.completionStatus === 'started'
            ? { lastSessionStartedAt: serverTimestamp() }
            : {}),
          ...(snapshot.completionStatus !== 'started'
            ? {
                lastSessionEndedAt: serverTimestamp(),
                lastSessionSummary: snapshot.summary,
                lastTopicsCovered: snapshot.topicsCovered,
                strengths: snapshot.strengths,
                struggles: snapshot.struggles,
                misconceptions: snapshot.misconceptions || [],
                diagnoses: snapshot.diagnoses || [],
                supportStrategies: snapshot.supportStrategies || [],
                recentWins: snapshot.recentWins,
                preferredTeachingModes: snapshot.preferredTeachingModes,
                teachingMethodsUsed: snapshot.teachingMethodsUsed,
                recommendedNextSteps: snapshot.recommendedNextSteps || [],
                learningPace: snapshot.learningPace || '',
                confidenceTrend: snapshot.confidenceTrend || '',
                reflectionConfidence: snapshot.reflectionConfidence || '',
                currentLessonObjective: snapshot.sessionFocus || '',
              }
            : {}),
          updatedAt: serverTimestamp(),
          ...(snapshot.topicsCovered.length ? { topicsSeen: arrayUnion(...snapshot.topicsCovered) } : {}),
          ...(snapshot.preferredTeachingModes.length
            ? { preferredModesSeen: arrayUnion(...snapshot.preferredTeachingModes) }
            : {}),
          ...(snapshot.completionStatus === 'started' ? { sessionCount: increment(1) } : {}),
        },
        { merge: true }
      ),
      setDoc(
        sessionRef,
        {
          sessionId: snapshot.sessionId,
          studentId: snapshot.studentId,
          studentName: snapshot.studentName,
          source: 'firefly',
          status: snapshot.completionStatus,
          startedAtMs: snapshot.startedAtMs || null,
          ...(snapshot.completionStatus !== 'started'
            ? {
                endedAt: serverTimestamp(),
                endedAtMs: snapshot.endedAtMs || Date.now(),
                durationSeconds: snapshot.durationSeconds,
                summary: snapshot.summary,
                topicsCovered: snapshot.topicsCovered,
                strengths: snapshot.strengths,
                struggles: snapshot.struggles,
                misconceptions: snapshot.misconceptions || [],
                diagnoses: snapshot.diagnoses || [],
                supportStrategies: snapshot.supportStrategies || [],
                recentWins: snapshot.recentWins,
                teachingMethodsUsed: snapshot.teachingMethodsUsed,
                preferredTeachingModes: snapshot.preferredTeachingModes,
                recommendedNextSteps: snapshot.recommendedNextSteps || [],
                learningPace: snapshot.learningPace || '',
                confidenceTrend: snapshot.confidenceTrend || '',
                reflectionConfidence: snapshot.reflectionConfidence || '',
                sessionFocus: snapshot.sessionFocus || '',
                boardArtifactTypes: snapshot.boardArtifactTypes,
                userTurns: snapshot.userTurns,
                assistantTurns: snapshot.assistantTurns,
              }
            : {}),
          ...(snapshot.completionStatus === 'started' ? { startedAt: serverTimestamp() } : {}),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      ),
      ...topicWrites,
    ])
  }, [])

  const storePendingPlmSnapshot = useCallback((snapshot) => {
    if (!snapshot || typeof window === 'undefined') return
    try {
      window.localStorage.setItem(PENDING_PLM_STORAGE_KEY, JSON.stringify(snapshot))
    } catch (error) {}
  }, [])

  const clearPendingPlmSnapshot = useCallback(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(PENDING_PLM_STORAGE_KEY)
    } catch (error) {}
  }, [])

  const flushPendingPlmSnapshot = useCallback(async () => {
    if (typeof window === 'undefined' || isFlushingPendingPlmRef.current) return

    const raw = window.localStorage.getItem(PENDING_PLM_STORAGE_KEY)
    if (!raw) return

    try {
      const snapshot = JSON.parse(raw)
      if (!snapshot?.studentId || !snapshot?.sessionId) {
        clearPendingPlmSnapshot()
        return
      }

      isFlushingPendingPlmRef.current = true
      await persistPlmSnapshot(snapshot)
      clearPendingPlmSnapshot()
    } catch (error) {
    } finally {
      isFlushingPendingPlmRef.current = false
    }
  }, [clearPendingPlmSnapshot, persistPlmSnapshot])

  const beginPlmSession = useCallback(async () => {
    const user = auth.currentUser || currentUser
    if (!user?.uid) return

    const sessionId = createId()
    plmSessionIdRef.current = sessionId
    plmSessionStartedAtRef.current = Date.now()

    try {
      await persistPlmSnapshot({
        version: 1,
        sessionId,
        studentId: user.uid,
        studentName: user.displayName || user.email || 'Student',
        studentEmail: user.email || '',
        completionStatus: 'started',
        topicsCovered: [],
        preferredTeachingModes: [],
        strengths: [],
        struggles: [],
        misconceptions: [],
        diagnoses: [],
        supportStrategies: [],
        recentWins: [],
        teachingMethodsUsed: [],
        recommendedNextSteps: [],
        learningPace: '',
        confidenceTrend: '',
        reflectionConfidence: '',
        sessionFocus: studyFocus.trim(),
        boardArtifactTypes: [],
      })
    } catch (error) {}
  }, [auth, currentUser, persistPlmSnapshot, studyFocus])

  const finalizePlmSession = useCallback(async (snapshot) => {
    if (!snapshot) return

    try {
      const reflection = await requestPlmReflection(snapshot)
      const enrichedSnapshot = mergeReflectionIntoSnapshot(snapshot, reflection)
      await persistPlmSnapshot(enrichedSnapshot)
      await loadLearnerMemory(enrichedSnapshot.studentId)
      clearPendingPlmSnapshot()
      plmSessionIdRef.current = ''
      plmSessionStartedAtRef.current = 0
      return enrichedSnapshot
    } catch (error) {
      storePendingPlmSnapshot(snapshot)
      return snapshot
    }
  }, [
    clearPendingPlmSnapshot,
    loadLearnerMemory,
    mergeReflectionIntoSnapshot,
    persistPlmSnapshot,
    requestPlmReflection,
    storePendingPlmSnapshot,
  ])

  const appendMessage = (message) => {
    setMessages((current) => [...current, message])
  }

  const resumeRealtimeAudioPlayback = () => {
    const audioElement = realtimeAudioRef.current
    if (!audioElement) return

    audioElement.muted = false
    const playAttempt = audioElement.play?.()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {})
    }
  }

  const copyToClipboard = async (value, successMessage) => {
    if (!navigator?.clipboard?.writeText) return
    try {
      await navigator.clipboard.writeText(value)
      replaceSystemMessage(successMessage)
    } catch (error) {}
  }

  const explainAnalogyInVoice = (definition, conceptSource = '') => {
    if (voiceStatus !== 'live' || !definition) return false

    const explanationPrompt = [
      'A new analogy lesson has just been placed on the whiteboard.',
      conceptSource ? `Original concept: ${conceptSource}` : '',
      definition.title ? `Analogy title: ${definition.title}` : '',
      definition.storyHook ? `Story hook: ${definition.storyHook}` : '',
      definition.takeaway ? `Key takeaway: ${definition.takeaway}` : '',
      'Explain the analogy aloud like a great teacher.',
      'Connect the story back to the real concept clearly.',
      'Be warm, memorable, and easy to follow.',
      'Do not mention scene numbers, JSON, or technical formatting.',
      'Assume the student can already see the explainer on the whiteboard.',
    ].filter(Boolean).join('\n\n')

    try {
      sendRealtimeVisualTurn({
        prompt: explanationPrompt,
        responseConfig: {
          modalities: ['audio', 'text'],
        },
      })
      replaceSystemMessage('Analogy lesson added to the whiteboard. Firefly is now explaining it aloud.')
      return true
    } catch (error) {
      return false
    }
  }

  const findLatestConceptSource = () => {
    const recentUserPrompt = [...messages]
      .reverse()
      .find((message) => message.role === 'user' && !isAnalogyWhiteboardRequest(message.content))

    if (recentUserPrompt?.content?.trim()) {
      return recentUserPrompt.content.trim()
    }

    const recentAssistantExplanation = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant' && message.source !== 'system' && !hasRenderableBlocks(message.content))

    return recentAssistantExplanation?.content?.trim() || ''
  }

  const handleAnalogyRequest = async ({ requestedPrompt = '' } = {}) => {
    if (isGeneratingAnalogy) return

    const commandConcept = extractConceptFromAnalogyPrompt(requestedPrompt)
    const conceptSource =
      commandConcept ||
      chatInput.trim() ||
      latestBoardEntry?.content?.trim() ||
      latestBoardEntry?.title?.trim() ||
      findLatestConceptSource()

    if (!conceptSource) {
      replaceSystemMessage('Ask about a concept first, or put the concept in the input box, then use the analogy button.')
      return
    }

    setIsGeneratingAnalogy(true)
    setBoardTool('view')
    setBoardPendingState({
      title: 'Analogy loading',
      message: `Building an illustrated analogy for ${conceptSource.slice(0, 80)}. Give me a moment.`,
      previousBoardEntryId: latestBoardEntry?.id || '',
    })
    clearBoardCanvas(false)
    replaceSystemMessage('Building an analogy on the whiteboard. Give me a moment.')
    try {
      let startedVoiceExplanation = false

      await requestChatReply({
        prompt: [
          `Use a funny, memorable, classroom-safe analogy to explain this concept: ${conceptSource}`,
          'Make it easy for a student to remember and genuinely understand.',
          'Return the answer as a firefly-analogy block for the whiteboard.',
          'Each scene should describe a simple illustrated visual that can be shown on the whiteboard.',
          'The video will use captions instead of voiceover, so make each scene explanation concise and easy to read.',
        ].join('\n\n'),
        assistantSource: 'chat',
        generateAnalogyImages: true,
        afterReply: (reply) => {
          const analogyBlock = parseMessageContent(reply).find((block) => block.type === 'analogy')
          if (!analogyBlock) return

          const definition = safeJsonParse(analogyBlock.value)
          if (!definition) return

          startedVoiceExplanation = explainAnalogyInVoice(definition, conceptSource)
        },
      })
      if (!startedVoiceExplanation) {
        replaceSystemMessage('Analogy lesson added to the whiteboard.')
      }
    } catch (error) {
      setBoardPendingState(null)
      appendMessage({
        id: createId(),
        role: 'assistant',
        source: 'system',
        content: error?.message || 'Firefly could not generate the analogy lesson right now.',
      })
    } finally {
      setIsGeneratingAnalogy(false)
    }
  }

  const syncBoardCanvas = (preserveDrawing = true) => {
    const canvas = boardCanvasRef.current
    const stage = boardStageRef.current
    if (!canvas || !stage) return

    const rect = stage.getBoundingClientRect()
    if (!rect.width || !rect.height) return

    const previousImage = preserveDrawing && canvas.width && canvas.height ? canvas.toDataURL() : ''
    const dpr = window.devicePixelRatio || 1
    const width = Math.max(Math.floor(rect.width), 1)
    const height = Math.max(Math.floor(rect.height), 1)
    const context = canvas.getContext('2d')

    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(height * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${height}px`
    context.setTransform(1, 0, 0, 1, 0, 0)
    context.clearRect(0, 0, canvas.width, canvas.height)
    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    context.lineCap = 'round'
    context.lineJoin = 'round'

    if (previousImage) {
      const image = new Image()
      image.onload = () => {
        context.clearRect(0, 0, width, height)
        context.drawImage(image, 0, 0, width, height)
      }
      image.src = previousImage
    }
  }

  const getBoardPoint = (event) => {
    const canvas = boardCanvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  const drawBoardStroke = (start, end) => {
    const canvas = boardCanvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')

    context.save()
    context.globalCompositeOperation = boardTool === 'eraser' ? 'destination-out' : 'source-over'
    context.strokeStyle = boardInkColor
    context.lineWidth = boardStrokeWidth
    context.beginPath()
    context.moveTo(start.x, start.y)
    context.lineTo(end.x, end.y)
    context.stroke()
    context.restore()
  }

  const handleBoardPointerDown = (event) => {
    if (!isBoardInkVisible || boardTool === 'view') return
    const point = getBoardPoint(event)
    boardDrawingStateRef.current = {
      isDrawing: true,
      pointerId: event.pointerId,
      point,
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    drawBoardStroke(point, { x: point.x + 0.01, y: point.y + 0.01 })
  }

  const handleBoardPointerMove = (event) => {
    const drawing = boardDrawingStateRef.current
    if (!drawing.isDrawing || drawing.pointerId !== event.pointerId) return
    const nextPoint = getBoardPoint(event)
    drawBoardStroke(drawing.point, nextPoint)
    boardDrawingStateRef.current = {
      ...drawing,
      point: nextPoint,
    }
  }

  const endBoardDrawing = (event) => {
    const drawing = boardDrawingStateRef.current
    if (drawing.pointerId && event?.currentTarget?.hasPointerCapture?.(drawing.pointerId)) {
      event.currentTarget.releasePointerCapture(drawing.pointerId)
    }
    boardDrawingStateRef.current = {
      isDrawing: false,
      pointerId: null,
      point: drawing.point,
    }
  }

  const clearBoardCanvas = (shouldNotify = true) => {
    const canvas = boardCanvasRef.current
    if (!canvas) return
    const context = canvas.getContext('2d')
    context.clearRect(0, 0, canvas.width, canvas.height)
    if (shouldNotify) {
      replaceSystemMessage('Board markup cleared.')
    }
  }

  const renderMessageBlocks = (content) => {
    return parseMessageContent(content).map((block, index) => {
      return renderRenderableBlock(block, index)
    })
  }

  const replaceSystemMessage = (message) => {
    setSystemMessage(message)
    if (!message) return
    window.clearTimeout(replaceSystemMessage.timeoutId)
    replaceSystemMessage.timeoutId = window.setTimeout(() => setSystemMessage(''), 4500)
  }

  const getHistoryPayload = () => {
    return messages
      .filter((message) => message.role === 'user' || message.role === 'assistant')
      .slice(-4)
      .map((message) => ({
        role: message.role,
        content: message.content,
      }))
  }

  const speakText = (text) => {
    if (!speakReplies || typeof window === 'undefined' || !window.speechSynthesis || !text) return
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 1
    utterance.pitch = 1
    window.speechSynthesis.speak(utterance)
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || null)
      if (user?.uid) {
        loadLearnerMemory(user.uid).catch(() => {})
        loadLatestProfileRecommendation(user.uid).catch(() => {})
        loadPreviousSessionMeta(user.uid).catch(() => {})
      } else {
        setLearnerMemory(null)
        setRecommendedStudyFocus('')
        setRecommendedFocusMeta(null)
        setPreviousSessionMeta(null)
        setStudyFocus('')
      }
    })

    return () => unsubscribe()
  }, [auth, loadLatestProfileRecommendation, loadLearnerMemory, loadPreviousSessionMeta])

  useEffect(() => {
    if (!currentUser?.uid) return
    flushPendingPlmSnapshot().catch(() => {})
  }, [currentUser, flushPendingPlmSnapshot])

  useEffect(() => {
    latestPlmSnapshotRef.current =
      tutorState === 'live'
        ? buildPlmSessionSnapshot({
            completionStatus: 'interrupted',
          })
        : null
  }, [tutorState, currentUser, messages, boardEntries, voiceStatus, screenStatus, cameraStatus, buildPlmSessionSnapshot])

  useEffect(() => {
    latestVisualStateRef.current = {
      activeVisualSource,
      screenStatus,
      cameraStatus,
      studyFocus,
      codeContext,
    }
  }, [activeVisualSource, screenStatus, cameraStatus, studyFocus, codeContext])

  useEffect(() => {
    const handleBeforeUnload = () => {
      storePendingPlmSnapshot(latestPlmSnapshotRef.current)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [storePendingPlmSnapshot])

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
      return
    }

    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
    }
  }, [isTranscriptVisible, messages])

  useEffect(() => {
    syncBoardCanvas(false)
  }, [latestBoardEntry])

  useEffect(() => {
    if (!boardPendingState) return

    if (
      (boardPendingState.previousBoardEntryId && latestBoardEntry?.id && latestBoardEntry.id !== boardPendingState.previousBoardEntryId) ||
      (!boardPendingState.previousBoardEntryId && latestBoardEntry?.id)
    ) {
      setBoardPendingState(null)
    }
  }, [boardPendingState, latestBoardEntry])

  useEffect(() => {
    if (latestBoardEntry?.type === 'analogy') {
      setBoardTool('view')
    }
  }, [latestBoardEntry])

  useEffect(() => {
    const stage = boardStageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return undefined

    syncBoardCanvas(false)

    const observer = new ResizeObserver(() => syncBoardCanvas(true))
    observer.observe(stage)

    return () => {
      observer.disconnect()
    }
  }, [latestBoardEntry, isBoardInkVisible])

  const stopScreenCapture = () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach((track) => track.stop())
      screenStreamRef.current = null
    }

    if (screenPreviewRef.current) {
      screenPreviewRef.current.srcObject = null
    }

    setScreenStatus('idle')
    if (activeVisualSource === 'screen' && cameraStatus === 'live') {
      setActiveVisualSource('camera')
    }
  }

  const stopCameraCapture = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop())
      cameraStreamRef.current = null
    }

    if (cameraPreviewRef.current) {
      cameraPreviewRef.current.srcObject = null
    }

    setCameraStatus('idle')
    if (activeVisualSource === 'camera' && screenStatus === 'live') {
      setActiveVisualSource('screen')
    }
  }

  const disconnectRealtimeVoice = () => {
    if (realtimeChannelRef.current) {
      try {
        realtimeChannelRef.current.close()
      } catch (error) {}
      realtimeChannelRef.current = null
    }

    if (realtimePeerRef.current) {
      try {
        realtimePeerRef.current.close()
      } catch (error) {}
      realtimePeerRef.current = null
    }

    if (realtimeLocalStreamRef.current) {
      realtimeLocalStreamRef.current.getTracks().forEach((track) => track.stop())
      realtimeLocalStreamRef.current = null
    }

    if (realtimeAudioRef.current) {
      realtimeAudioRef.current.srcObject = null
    }

    setVoiceStatus('idle')
  }

  const resetTutorWorkspace = () => {
    disconnectRealtimeVoice()
    stopScreenCapture()
    stopCameraCapture()
    seenVoiceUserItemsRef.current = new Set()
    seenVoiceAssistantItemsRef.current = new Set()
    setVoiceError('')
    setPageError('')
    setIncludeVisualInNextTurn(false)
    setTutorState('idle')
    setMessages(initialMessages)
    setStudyFocus(recommendedStudyFocus || '')
    setScreenStatus('idle')
    setCameraStatus('idle')
    setVoiceStatus('idle')
    setActiveVisualSource('screen')
    clearBoardCanvas()
  }

  const capturePreviewFrame = (previewRef, canvasRef, missingMessage) => {
    const preview = previewRef.current
    const canvas = canvasRef.current

    if (!preview || !canvas || !preview.videoWidth || !preview.videoHeight) {
      throw new Error(missingMessage)
    }

    const { videoWidth, videoHeight } = preview
    const longestEdge = Math.max(videoWidth, videoHeight)
    const scale = longestEdge > MAX_VISUAL_SNAPSHOT_EDGE ? MAX_VISUAL_SNAPSHOT_EDGE / longestEdge : 1
    const captureWidth = Math.max(1, Math.round(videoWidth * scale))
    const captureHeight = Math.max(1, Math.round(videoHeight * scale))

    canvas.width = captureWidth
    canvas.height = captureHeight
    const context = canvas.getContext('2d')
    context.drawImage(preview, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', VISUAL_SNAPSHOT_QUALITY)
  }

  const attachStreamToPreview = async (previewRef, stream, { muted = true } = {}) => {
    const preview = previewRef.current
    if (!preview) return

    preview.srcObject = null
    preview.muted = muted
    preview.playsInline = true
    preview.autoplay = true
    preview.srcObject = stream

    if (preview.readyState < 1) {
      await new Promise((resolve) => {
        const handleReady = () => {
          preview.removeEventListener('loadedmetadata', handleReady)
          resolve()
        }

        preview.addEventListener('loadedmetadata', handleReady)
      })
    }

    const playAttempt = preview.play?.()
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {})
    }
  }

  const captureVisualSnapshot = (source = activeVisualSource) => {
    if (source === 'camera') {
      return capturePreviewFrame(
        cameraPreviewRef,
        cameraCanvasRef,
        'Turn on your camera before asking Firefly to inspect it.'
      )
    }

    return capturePreviewFrame(
      screenPreviewRef,
      screenCanvasRef,
      'Share your screen before asking Firefly to inspect it.'
    )
  }

  const sendRealtimeVisualTurn = ({
    prompt,
    includeVisual = false,
    visualSource = activeVisualSource,
    responseConfig = null,
  }) => {
    const channel = realtimeChannelRef.current
    if (!channel || channel.readyState !== 'open') {
      throw new Error('Connect live voice before sending notes into the session.')
    }

    let screenshotDataUrl = ''
    if (includeVisual) {
      try {
        screenshotDataUrl = captureVisualSnapshot(visualSource)
      } catch (error) {
        screenshotDataUrl = ''
      }
    }

    const currentState = latestVisualStateRef.current
    const content = []

    if (screenshotDataUrl) {
      content.push({
        type: 'input_image',
        image_url: screenshotDataUrl,
      })
    }

    content.push({
      type: 'input_text',
      text: [
        prompt.trim(),
        screenshotDataUrl
          ? `The attached ${visualSource} snapshot is the latest visual context. Ground your answer in that image and do not guess details that are not visible.`
          : '',
        currentState.studyFocus.trim() ? `Learning focus:\n${currentState.studyFocus.trim()}` : '',
        currentState.codeContext.trim() ? `Project context:\n${currentState.codeContext.trim().slice(0, 5000)}` : '',
      ]
        .filter(Boolean)
        .join('\n\n'),
    })

    channel.send(
      JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content,
        },
      })
    )
    channel.send(
      JSON.stringify({
        type: 'response.create',
        ...(responseConfig ? { response: responseConfig } : {}),
      })
    )
    resumeRealtimeAudioPlayback()
  }

  const startTutorSession = () => {
    const trimmedFocus = studyFocus.trim()
    if (!trimmedFocus) {
      setPageError('Tell Firefly exactly where you need help before starting the lesson.')
      return
    }

    setTutorState('live')
    setPageError('')
    setMessages([
      initialMessages[0],
      {
        id: createId(),
        role: 'user',
        source: 'chat',
        content: `I need help with: ${trimmedFocus}`,
      },
      {
        id: createId(),
        role: 'assistant',
        source: 'chat',
        content: `We will stay focused on this lesson objective: ${trimmedFocus}\n\nI will guide you step by step like an instructor, ask for your screen when it will help, and keep bringing us back to this goal if we drift.`,
      },
    ])
    replaceSystemMessage('Firefly is ready. Start voice, and stay anchored to your lesson objective.')
    beginPlmSession().catch(() => {})
  }

  const endTutorSession = () => {
    const finalSnapshot = buildPlmSessionSnapshot({
      completionStatus: 'completed',
    })
    setIsGeneratingSessionReport(true)
    resetTutorWorkspace()
    replaceSystemMessage('Firefly session ended. Generating your report...')
    finalizePlmSession(finalSnapshot)
      .then((reportSnapshot) => {
        if (reportSnapshot) {
          setLastSessionReport(reportSnapshot)
        }
      })
      .finally(() => {
        setIsGeneratingSessionReport(false)
      })
  }

  const connectRealtimeVoice = async () => {
    if (voiceStatus === 'live' || voiceStatus === 'connecting') {
      disconnectRealtimeVoice()
      return
    }

    try {
      setVoiceStatus('connecting')
      setVoiceError('')
      replaceSystemMessage('Voice starting...')

      const tokenResponse = await fetch(buildApiUrl(TOKEN_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          instructions: realtimeInstructions,
          voice: selectedVoice,
        }),
      })

      const tokenData = await tokenResponse.json()
      if (!tokenResponse.ok) {
        throw new Error(tokenData?.error || 'Could not start the realtime instructor session.')
      }

      const ephemeralKey =
        tokenData?.value ||
        tokenData?.client_secret?.value ||
        tokenData?.client_secret ||
        tokenData?.token ||
        ''

      if (!ephemeralKey) {
        throw new Error('Firefly did not receive a realtime token.')
      }

      const peerConnection = new RTCPeerConnection()
      const audioElement = new Audio()
      audioElement.autoplay = true
      audioElement.muted = false
      realtimeAudioRef.current = audioElement

      peerConnection.ontrack = (event) => {
        audioElement.srcObject = event.streams[0]
        resumeRealtimeAudioPlayback()
      }

      const localStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      realtimeLocalStreamRef.current = localStream
      localStream.getTracks().forEach((track) => peerConnection.addTrack(track, localStream))

      const dataChannel = peerConnection.createDataChannel('oai-events')
      realtimePeerRef.current = peerConnection
      realtimeChannelRef.current = dataChannel

      dataChannel.addEventListener('open', () => {
        dataChannel.send(
          JSON.stringify({
            type: 'session.update',
            session: {
              type: 'realtime',
              audio: {
                input: {
                  turn_detection: {
                    type: 'server_vad',
                    create_response: false,
                    interrupt_response: true,
                  },
                },
              },
            },
          })
        )
        setVoiceStatus('live')
        replaceSystemMessage('Live voice is connected. Firefly now waits for each spoken turn, captures the latest visual when available, and then responds.')
      })

      dataChannel.addEventListener('message', (event) => {
        try {
          const payload = JSON.parse(event.data)

          if (
            payload.type === 'conversation.item.input_audio_transcription.completed' &&
            payload.transcript &&
            !seenVoiceUserItemsRef.current.has(payload.item_id)
          ) {
            seenVoiceUserItemsRef.current.add(payload.item_id)
            appendMessage({
              id: payload.item_id || createId(),
              role: 'user',
              source: 'voice',
              content: payload.transcript,
            })

            const { activeVisualSource: source, screenStatus: currentScreenStatus, cameraStatus: currentCameraStatus } =
              latestVisualStateRef.current
            const hasLiveVisual =
              (source === 'screen' && currentScreenStatus === 'live') ||
              (source === 'camera' && currentCameraStatus === 'live')

            if (isAnalogyWhiteboardRequest(payload.transcript)) {
              replaceSystemMessage('Building the analogy on the whiteboard. Give me a moment.')
              handleAnalogyRequest({
                requestedPrompt: payload.transcript,
              }).catch((error) => {
                appendMessage({
                  id: createId(),
                  role: 'assistant',
                  source: 'system',
                  content: error?.message || 'Firefly could not build the whiteboard analogy right now.',
                })
              })
              return
            }

            if (shouldPreferRichResponse(payload.transcript)) {
              replaceSystemMessage('Rendering the visual or code answer in chat below.')
              requestChatReply({
                prompt: payload.transcript,
                includeVisual: hasLiveVisual,
                visualSource: source,
                assistantSource: hasLiveVisual ? source : 'chat',
              }).catch((error) => {
                appendMessage({
                  id: createId(),
                  role: 'assistant',
                  source: 'system',
                  content: error?.message || 'Firefly could not render the visual answer right now.',
                })
              })
              return
            }

            sendRealtimeVisualTurn({
              prompt: payload.transcript,
              includeVisual: hasLiveVisual,
              visualSource: source,
            })
          }

          if (
            payload.type === 'response.output_audio_transcript.done' &&
            payload.transcript &&
            !seenVoiceAssistantItemsRef.current.has(payload.item_id)
          ) {
            seenVoiceAssistantItemsRef.current.add(payload.item_id)
            appendMessage({
              id: payload.item_id || createId(),
              role: 'assistant',
              source: 'voice',
              content: payload.transcript,
            })
          }
        } catch (error) {}
      })

      const offer = await peerConnection.createOffer()
      await peerConnection.setLocalDescription(offer)

      const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      })

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text()
        throw new Error(errorText || 'OpenAI rejected the realtime connection.')
      }

      await peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: await sdpResponse.text(),
      })
    } catch (error) {
      disconnectRealtimeVoice()
      setVoiceStatus('error')
      setVoiceError(error?.message || 'Live voice could not be started.')
    }
  }

  const startScreenCapture = async () => {
    try {
      stopScreenCapture()
      setScreenStatus('connecting')
      setPageError('')
      setVoiceError('')

      if (!navigator?.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen sharing is not supported in this browser.')
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      })

      const [track] = stream.getVideoTracks()
      if (track) {
        track.contentHint = 'detail'
      }

      screenStreamRef.current = stream
      track.onended = () => stopScreenCapture()

      await attachStreamToPreview(screenPreviewRef, stream, { muted: true })

      setScreenStatus('live')
      setActiveVisualSource('screen')
      replaceSystemMessage('Screen preview is live. Use Analyze Active View or include the visual in your next prompt so Firefly actually sees it.')
    } catch (error) {
      setScreenStatus(error?.name === 'NotAllowedError' ? 'idle' : 'error')
      appendMessage({
        id: createId(),
        role: 'assistant',
        source: 'system',
        content: error.message || 'Screen sharing was not started.',
      })
    }
  }

  const startCameraCapture = async () => {
    try {
      stopCameraCapture()
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      })

      cameraStreamRef.current = stream
      const [track] = stream.getVideoTracks()
      track.onended = () => stopCameraCapture()

      await attachStreamToPreview(cameraPreviewRef, stream, { muted: true })

      setCameraStatus('live')
      setActiveVisualSource('camera')
      replaceSystemMessage('Camera preview is live. Use Analyze Active View or include the visual in your next prompt so Firefly actually sees it.')
    } catch (error) {
      setCameraStatus('error')
      appendMessage({
        id: createId(),
        role: 'assistant',
        source: 'system',
        content: error.message || 'Camera could not be started.',
      })
    }
  }

  const requestChatReply = async ({
    prompt,
    includeVisual = false,
    visualSource = activeVisualSource,
    assistantSource,
    afterReply,
    generateAnalogyImages = false,
  }) => {
    let screenshotDataUrl = ''
    if (includeVisual) {
      replaceSystemMessage(`Inspecting your ${visualSource}...`)
      screenshotDataUrl = captureVisualSnapshot(visualSource)
    }
    const fetchReply = async (message, historyOverride) => {
      const response = await fetch(buildApiUrl(RESPOND_PATH), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          studyFocus,
          codeContext,
          history: historyOverride ?? getHistoryPayload(),
          screenshotDataUrl,
          generateAnalogyImages,
        }),
      })

      const data = await response.json()
      if (!response.ok) {
        throw new Error(data?.error || 'Firefly could not answer right now.')
      }

      return data?.reply || 'I could not generate a reply yet.'
    }

    const prefersRichResponse = shouldRetryRenderableResponse(prompt)
    let reply = await fetchReply(buildTutorPrompt(prompt, learnerMemory))

    if (prefersRichResponse && !hasRenderableBlocks(reply)) {
      const retryReply = await fetchReply(buildRichRetryPrompt(prompt), [])
      reply = hasRenderableBlocks(retryReply)
        ? retryReply
        : await fetchReply(buildStrictRenderableConversionPrompt(prompt, reply), [])
    }

    appendMessage({
      id: createId(),
      role: 'assistant',
      source: assistantSource || (includeVisual ? visualSource : 'chat'),
      content: reply,
    })

    speakText(reply)
    afterReply?.(reply)
  }

  const sendTutorRequest = async ({ prompt, includeVisual = false, toRealtime = false, visualSource = activeVisualSource }) => {
    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt || isSending) return
    const prefersRichResponse = shouldPreferRichResponse(trimmedPrompt)

    appendMessage({
      id: createId(),
      role: 'user',
      source: includeVisual ? visualSource : toRealtime && !prefersRichResponse ? 'voice' : 'chat',
      content: trimmedPrompt,
    })

    setChatInput('')

    if (isAnalogyWhiteboardRequest(trimmedPrompt)) {
      await handleAnalogyRequest({
        requestedPrompt: trimmedPrompt,
      })
      setIncludeVisualInNextTurn(false)
      return
    }

    if (toRealtime && !prefersRichResponse) {
      try {
        if (includeVisual) {
          replaceSystemMessage(`Inspecting your ${visualSource} in the live voice session...`)
        }
        sendRealtimeVisualTurn({
          prompt: trimmedPrompt,
          includeVisual,
          visualSource,
        })
      } catch (error) {
        setVoiceError(error?.message || 'Connect live voice before sending notes into the session.')
        return
      }

      replaceSystemMessage(
        includeVisual
          ? `Sent to the live voice session with the current ${visualSource} snapshot.`
          : 'Sent to the live voice session.'
      )
      setIncludeVisualInNextTurn(false)
      return
    }

    setIsSending(true)
    if (toRealtime && prefersRichResponse) {
      replaceSystemMessage('Rendering this answer in chat so code, diagrams, charts, and math display correctly.')
    }

    try {
      await requestChatReply({
        prompt: trimmedPrompt,
        includeVisual,
        visualSource,
        assistantSource: includeVisual ? visualSource : 'chat',
      })
    } catch (error) {
      appendMessage({
        id: createId(),
        role: 'assistant',
        source: 'system',
        content: error.message || 'Firefly could not answer right now.',
      })
    } finally {
      setIsSending(false)
      setIncludeVisualInNextTurn(false)
    }
  }

  const analyzeCurrentVisual = async () => {
    if (isAnalyzingVisual) return
    setIsAnalyzingVisual(true)
    try {
      const source = activeVisualSource === 'camera' && cameraStatus === 'live'
        ? 'camera'
        : 'screen'

      replaceSystemMessage(`Inspecting your ${source}...`)

      await sendTutorRequest({
        prompt:
          chatInput.trim() ||
          (source === 'camera'
            ? 'Look through my camera, describe what you see, and tell me the most useful next step.'
            : 'Look at my current screen, explain what you see, and tell me the most useful next step.'),
        includeVisual: true,
        visualSource: source,
      })
    } finally {
      setIsAnalyzingVisual(false)
    }
  }

  useEffect(() => {
    let ignore = false

    const checkHealth = async () => {
      try {
        const response = await fetch(buildApiUrl(HEALTH_PATH))
        if (!response.ok) throw new Error('offline')
        if (!ignore) setHealthStatus('online')
      } catch (error) {
        if (!ignore) setHealthStatus('offline')
      }
    }

    checkHealth()

    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    return () => {
      storePendingPlmSnapshot(latestPlmSnapshotRef.current)

      if (realtimeChannelRef.current) {
        try {
          realtimeChannelRef.current.close()
        } catch (error) {}
      }

      if (realtimePeerRef.current) {
        try {
          realtimePeerRef.current.close()
        } catch (error) {}
      }

      if (realtimeLocalStreamRef.current) {
        realtimeLocalStreamRef.current.getTracks().forEach((track) => track.stop())
      }

      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach((track) => track.stop())
      }

      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop())
      }

      if (typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel()
      }
    }
  }, [storePendingPlmSnapshot])

  if (tutorState === 'idle') {
    return (
      <div className={`AiComp fireflyPage fireflyPageCentered ${lastSessionReport || isGeneratingSessionReport ? 'fireflyPageStacked' : ''}`}>
        <section className='fireflyLaunchCard'>
          <span className='fireflyEyebrow'>Ubunifu Firefly</span>
          <h1>Launch Ubunifu Firefly</h1>
          <p>
            Firefly is your AI teaching assistant for adaptive learning, helping you revisit class concepts,
            brush up on gaps, and get one-on-one realtime tutoring with voice and visual guidance.
          </p>

          <div className={`fireflyLaunchMeta fireflyLaunchSignal firefly-${healthStatus}`}>
            <span className='fireflySignalOrb' aria-hidden='true' />
            <span>
              {healthStatus === 'online'
                ? 'Firefly systems online'
                : healthStatus === 'checking'
                  ? 'Firefly systems waking up'
              : 'Firefly systems offline'}
            </span>
          </div>

          {recommendedFocusMeta ? (
            <div className='fireflyRecommendationInline'>
              Help for <strong>{recommendedFocusMeta.lessonObjective || 'a recorded learning gap'}</strong> was
              recorded on <strong>{formatRecommendationDate(recommendedFocusMeta.updatedAtMs)}</strong>. Join Firefly
              now for guided support on this area.
            </div>
          ) : previousSessionMeta?.startedAtMs || previousSessionMeta?.endedAtMs ? (
            <div className='fireflyRecommendationInline'>
              Your previous Firefly session started at <strong>{formatRecommendationDateTime(previousSessionMeta.startedAtMs)}</strong>
              {previousSessionMeta.endedAtMs ? (
                <> and ended at <strong>{formatRecommendationDateTime(previousSessionMeta.endedAtMs)}</strong>.</>
              ) : (
                <>.</>
              )}
              {' '}No new Firefly reports.{' '}
              <Link to='/firefly-reports' className='fireflyInlineLink'>Click here to view your previous reports</Link>.
            </div>
          ) : (
            <label className='fireflyLaunchFocusField'>
              <textarea
                value={studyFocus}
                onChange={(event) => {
                  setStudyFocus(event.target.value)
                  if (pageError) setPageError('')
                }}
                placeholder='Example: I am stuck on React state updates in my login form and I need help debugging why the dashboard is not rendering after sign in.'
                rows={4}
              />
              <small>Start with one focused issue. Firefly can teach with voice, screen share, camera context, and analogies while adapting the lesson around your objective.</small>
            </label>
          )}

          {pageError ? <p className='fireflyInlineError'>{pageError}</p> : null}

          {recommendedFocusMeta || !previousSessionMeta ? (
            <button
              type='button'
              className='fireflyPrimaryBtn fireflyLaunchBtn'
              onClick={startTutorSession}
              disabled={healthStatus === 'offline' || !studyFocus.trim()}
            >
              <FontAwesomeIcon icon={faArrowRight} />
              Start Firefly
            </button>
          ) : null}
        </section>

        {isGeneratingSessionReport ? (
          <section className='fireflySessionReportCard fireflySessionReportPending'>
            <div className='fireflySessionReportHeader'>
              <span className='fireflyEyebrow'>Session Report</span>
              <h2>Generating your learning report...</h2>
              <p>Firefly is turning the lesson into a student-friendly summary with strengths, weak spots, and next steps.</p>
            </div>
          </section>
        ) : null}

        {lastSessionReport ? (
          <section className='fireflySessionReportCard'>
            <div className='fireflySessionReportHeader'>
              <span className='fireflyEyebrow'>Session Report</span>
              <h2>Your Firefly lesson report</h2>
              <p>{lastSessionReport.summary || 'Here is a summary of how the lesson went and where to focus next.'}</p>
            </div>

            <div className='fireflySessionReportMeta'>
              {lastSessionReport.sessionFocus ? (
                <div className='fireflyReportStat'>
                  <span>Lesson objective</span>
                  <strong>{lastSessionReport.sessionFocus}</strong>
                </div>
              ) : null}
              <div className='fireflyReportStat'>
                <span>Duration</span>
                <strong>{formatSessionDuration(lastSessionReport.durationSeconds)}</strong>
              </div>
              <div className='fireflyReportStat'>
                <span>Topics</span>
                <strong>{lastSessionReport.topicsCovered?.length || 0}</strong>
              </div>
              <div className='fireflyReportStat'>
                <span>Confidence</span>
                <strong>{lastSessionReport.confidenceTrend || 'steady'}</strong>
              </div>
            </div>

            {lastSessionReport.topicsCovered?.length ? (
              <div className='fireflyReportSection'>
                <h3>Topics covered</h3>
                <div className='fireflyReportChipRow'>
                  {lastSessionReport.topicsCovered.map((topic) => (
                    <span key={topic} className='fireflyReportChip'>{topic}</span>
                  ))}
                </div>
              </div>
            ) : null}

            {(lastSessionReport.strengths?.length || lastSessionReport.struggles?.length) ? (
              <div className='fireflyReportGrid'>
                <div className='fireflyReportSection'>
                  <h3>Strengths</h3>
                  <ul>
                    {(lastSessionReport.strengths?.length ? lastSessionReport.strengths : ['Firefly will keep building a stronger evidence base over the next few lessons.']).map((item, index) => (
                      <li key={`strength-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className='fireflyReportSection'>
                  <h3>Challenges</h3>
                  <ul>
                    {(lastSessionReport.struggles?.length ? lastSessionReport.struggles : ['No major struggle signals were captured in this session.']).map((item, index) => (
                      <li key={`challenge-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}

            {lastSessionReport.diagnoses?.length ? (
              <div className='fireflyReportSection'>
                <h3>Fine-grained diagnosis</h3>
                <div className='fireflyDiagnosisList'>
                  {lastSessionReport.diagnoses.map((item, index) => (
                    <article key={`diagnosis-${index}`} className='fireflyDiagnosisCard'>
                      <div className='fireflyDiagnosisLine'>
                        <span>Topic</span>
                        <strong>{item.topic || 'General lesson area'}</strong>
                      </div>
                      {item.subtopic ? (
                        <div className='fireflyDiagnosisLine'>
                          <span>Subtopic</span>
                          <strong>{item.subtopic}</strong>
                        </div>
                      ) : null}
                      {item.misconception ? (
                        <div className='fireflyDiagnosisBlock'>
                          <span>Misconception</span>
                          <p>{item.misconception}</p>
                        </div>
                      ) : null}
                      {item.evidence ? (
                        <div className='fireflyDiagnosisBlock'>
                          <span>Evidence</span>
                          <p>{item.evidence}</p>
                        </div>
                      ) : null}
                      {item.bestIntervention ? (
                        <div className='fireflyDiagnosisBlock'>
                          <span>Best intervention</span>
                          <p>{item.bestIntervention}</p>
                        </div>
                      ) : null}
                    </article>
                  ))}
                </div>
              </div>
            ) : null}

            {(lastSessionReport.recommendedNextSteps?.length || lastSessionReport.supportStrategies?.length) ? (
              <div className='fireflyReportGrid'>
                <div className='fireflyReportSection'>
                  <h3>What to do next</h3>
                  <ul>
                    {(lastSessionReport.recommendedNextSteps?.length
                      ? lastSessionReport.recommendedNextSteps
                      : ['Start the next Firefly session with one concrete practice problem from this lesson.']).map((item, index) => (
                      <li key={`next-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div className='fireflyReportSection'>
                  <h3>How Firefly should support you</h3>
                  <ul>
                    {(lastSessionReport.supportStrategies?.length
                      ? lastSessionReport.supportStrategies
                      : ['Firefly will keep using the teaching modes that seemed to help most in this lesson.']).map((item, index) => (
                      <li key={`support-${index}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
    )
  }

  return (
    <div className='AiComp fireflyPage'>
      <section className='fireflySessionBar fireflySessionHero'>
        <div className='fireflySessionMain'>
          <div className='fireflySessionIntro'>
            <h1>Firefly is ready.</h1>
          </div>

          {studyFocus.trim() ? (
            <div className='fireflyFocusBrief'>
              <span className='fireflyFocusBriefLabel'>Lesson objective</span>
              <p>{studyFocus.trim()}</p>
            </div>
          ) : null}

          <div className='fireflySessionTopRow'>
            <div className='fireflySessionActions fireflySessionStatusRow'>
              <div className={`fireflyStatusChip fireflySessionStatusChip firefly-${voiceStatus}`}>
                <span>Voice</span>
                <strong>{voiceStatus}</strong>
              </div>
              <div className={`fireflyStatusChip fireflySessionStatusChip firefly-${screenStatus}`}>
                <span>Screen</span>
                <strong>{screenStatus}</strong>
              </div>
              <div className={`fireflyStatusChip fireflySessionStatusChip firefly-${cameraStatus}`}>
                <span>Camera</span>
                <strong>{cameraStatus}</strong>
              </div>
            </div>

            <button type='button' className='fireflyDangerBtn fireflySessionEndBtn' onClick={endTutorSession}>
              <FontAwesomeIcon icon={faPhoneSlash} />
              End Tutor
            </button>
          </div>

          <div className='fireflySessionControlWrap'>
            <span className='fireflySessionLabel'>Session Controls</span>
            <div className='fireflyToolButtons fireflySessionButtons'>
              <button type='button' className='fireflySecondaryBtn fireflySessionPrimaryBtn' onClick={connectRealtimeVoice}>
              <FontAwesomeIcon icon={voiceStatus === 'live' ? faPhoneSlash : faMicrophoneLines} />
              {voiceStatus === 'live' ? 'Stop Voice' : voiceStatus === 'connecting' ? 'Voice Starting' : 'Start Voice'}
              </button>
              <button type='button' className='fireflyGhostBtn fireflySessionControlBtn' onClick={startScreenCapture}>
                <FontAwesomeIcon icon={faDisplay} />
                Share Screen
              </button>
              <button
                type='button'
                className='fireflyGhostBtn fireflySessionControlBtn'
                onClick={stopScreenCapture}
                disabled={screenStatus !== 'live'}
              >
                <FontAwesomeIcon icon={faStop} />
                Stop Screen
              </button>
              <button type='button' className='fireflyGhostBtn fireflySessionControlBtn' onClick={startCameraCapture}>
                <FontAwesomeIcon icon={faCamera} />
                Turn On Camera
              </button>
              <button
                type='button'
                className='fireflyGhostBtn fireflySessionControlBtn'
                onClick={stopCameraCapture}
                disabled={cameraStatus !== 'live'}
              >
                <FontAwesomeIcon icon={faStop} />
                Stop Camera
              </button>
            </div>
          </div>
        </div>
      </section>

      {systemMessage ? <p className='fireflyInlineNotice'>{systemMessage}</p> : null}
      {pageError ? <p className='fireflyInlineError'>{pageError}</p> : null}
      {voiceError ? <p className='fireflyInlineError'>{voiceError}</p> : null}

      <section className='fireflyTutorGrid fireflyTutorGridSingle'>
        <div className='fireflyBoardPanel'>
          <div className='fireflyBoardShell'>
            <div className='fireflyBoardHeader'>
              <div>
                <span className='fireflyBoardLabel'>
                  <FontAwesomeIcon icon={faChalkboard} />
                  Whiteboard
                </span>
                <p>Firefly promotes generated code, copyable notes, formulas, charts, diagrams, and illustrations onto the board.</p>
              </div>

              <button
                type='button'
                className='fireflyTranscriptToggle'
                onClick={() => setIsTranscriptVisible((current) => !current)}
              >
                <FontAwesomeIcon icon={faChevronDown} className={isTranscriptVisible ? 'fireflyTranscriptIconOpen' : ''} />
                {isTranscriptVisible ? 'Hide Transcript' : 'Show Transcript'}
              </button>
            </div>

            <div className='fireflyBoardContent'>
              <div className='fireflyBoardStageShell'>
                <div className='fireflyBoardToolbar'>
                  <div className='fireflyBoardToolGroup'>
                    <button
                      type='button'
                      className={`fireflyBoardToolBtn ${boardTool === 'view' ? 'fireflyBoardToolBtnActive' : ''}`}
                      onClick={() => setBoardTool('view')}
                    >
                      Watch
                    </button>
                    <button
                      type='button'
                      className={`fireflyBoardToolBtn ${boardTool === 'pen' ? 'fireflyBoardToolBtnActive' : ''}`}
                      onClick={() => setBoardTool('pen')}
                    >
                      <FontAwesomeIcon icon={faPen} />
                      Draw
                    </button>
                    <button
                      type='button'
                      className={`fireflyBoardToolBtn ${boardTool === 'eraser' ? 'fireflyBoardToolBtnActive' : ''}`}
                      onClick={() => setBoardTool('eraser')}
                    >
                      <FontAwesomeIcon icon={faEraser} />
                      Erase
                    </button>
                  </div>

                  <div className='fireflyBoardToolGroup'>
                    {boardInkPalette.map((color) => (
                      <button
                        key={color}
                        type='button'
                        className={`fireflyBoardColorSwatch ${boardInkColor === color ? 'fireflyBoardColorSwatchActive' : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => {
                          setBoardTool('pen')
                          setBoardInkColor(color)
                        }}
                        aria-label={`Use ${color} ink`}
                      />
                    ))}
                  </div>

                  <div className='fireflyBoardToolGroup'>
                    <button
                      type='button'
                      className='fireflyBoardToolBtn fireflyBoardAnalogyBtn'
                      onClick={handleAnalogyRequest}
                      disabled={isGeneratingAnalogy || isSending}
                    >
                      <FontAwesomeIcon icon={faLightbulb} />
                      {isGeneratingAnalogy ? 'Building Analogy...' : 'Use analogy'}
                    </button>
                  </div>

                  <div className='fireflyBoardToolGroup'>
                    {[2, 4, 6].map((size) => (
                      <button
                        key={size}
                        type='button'
                        className={`fireflyBoardSizeBtn ${boardStrokeWidth === size ? 'fireflyBoardSizeBtnActive' : ''}`}
                        onClick={() => setBoardStrokeWidth(size)}
                      >
                        {size === 2 ? 'Thin' : size === 4 ? 'Medium' : 'Bold'}
                      </button>
                    ))}
                    <button
                      type='button'
                      className='fireflyBoardToolBtn'
                      onClick={() => setIsBoardInkVisible((current) => !current)}
                    >
                      {isBoardInkVisible ? 'Hide Ink' : 'Show Ink'}
                    </button>
                    <button type='button' className='fireflyBoardToolBtn' onClick={clearBoardCanvas}>
                      <FontAwesomeIcon icon={faTrashCan} />
                      Clear
                    </button>
                  </div>
                </div>

                <div className='fireflyBoardStage' ref={boardStageRef}>
                  <div className='fireflyBoardStageInner'>
                    {boardPendingState ? (
                      <article className='fireflyBoardStageContent fireflyBoardStageContent-pending'>
                        <div className='fireflyBoardStageTop'>
                          <div>
                            <span className='fireflyBoardCardTag'>{boardPendingState.title}</span>
                            <strong>Whiteboard is preparing your analogy</strong>
                          </div>
                        </div>

                        <div className='fireflyBoardPendingCard'>
                          <div className='fireflyBoardPendingDots' aria-hidden='true'>
                            <span />
                            <span />
                            <span />
                          </div>
                          <p>{boardPendingState.message}</p>
                        </div>
                      </article>
                    ) : latestBoardEntry ? (
                      <article className={`fireflyBoardStageContent fireflyBoardStageContent-${latestBoardEntry.type}`}>
                        <div className='fireflyBoardStageTop'>
                          <div>
                            <span className='fireflyBoardCardTag'>{latestBoardEntry.title}</span>
                            <strong>Teaching Stage</strong>
                          </div>
                          <button
                            type='button'
                            className='fireflyBoardAction'
                            onClick={() =>
                              copyToClipboard(
                                latestBoardEntry.content,
                                latestBoardEntry.type === 'code' ? 'Code copied from the board.' : 'Board content copied.'
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faCopy} />
                            Copy
                          </button>
                        </div>

                        <div className='fireflyBoardStageBody'>
                          {renderRenderableBlock(latestBoardEntry, 'board-stage', {
                            showFallbackLabel: true,
                          })}
                        </div>
                      </article>
                    ) : (
                      <div className='fireflyBoardStagePlaceholder' />
                    )}
                  </div>

                  <canvas
                    ref={boardCanvasRef}
                    className={`fireflyBoardCanvas ${isBoardInkVisible ? '' : 'fireflyBoardCanvasHidden'} ${boardTool === 'view' ? 'fireflyBoardCanvasPassive' : ''}`}
                    onPointerDown={handleBoardPointerDown}
                    onPointerMove={handleBoardPointerMove}
                    onPointerUp={endBoardDrawing}
                    onPointerLeave={endBoardDrawing}
                  />
                </div>

                <p className='fireflyBoardStageHint'>Draw directly on the board to sketch a concept, underline something important, or show Firefly your thinking before you share the screen back.</p>
              </div>

              {pinnedBoardEntries.length ? (
                <div className='fireflyBoardPinnedSection'>
                  <div className='fireflyBoardPinnedHeader'>
                    <span className='fireflyBoardCardTag'>Pinned Materials</span>
                    <strong>Recent lesson outputs</strong>
                  </div>

                  {pinnedBoardEntries.map((entry, index) => (
                    <article key={entry.id} className={`fireflyBoardCard fireflyBoardCard-${entry.type}`}>
                      <div className='fireflyBoardCardTop'>
                        <div>
                          <span className='fireflyBoardCardTag'>{entry.title}</span>
                          <strong>{`Pinned item ${index + 1}`}</strong>
                        </div>
                        <button
                          type='button'
                          className='fireflyBoardAction'
                          onClick={() =>
                            copyToClipboard(
                              entry.content,
                              entry.type === 'code' ? 'Code copied from the board.' : 'Board content copied.'
                            )
                          }
                        >
                          <FontAwesomeIcon icon={faCopy} />
                          Copy
                        </button>
                      </div>

                      <div className='fireflyBoardCardBody'>
                        {renderRenderableBlock(entry, `board-${index}`, {
                          showFallbackLabel: true,
                        })}
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section className='fireflyRoomGrid fireflyVisualGrid'>
        <div className='fireflyRoomPanel fireflyStudioPanel'>
          <div className='fireflyStudioHeader'>
            <div className='fireflyStudioTitle'>
              <span className='fireflyPanelTag'>Workspace View</span>
              <h2>Screen Preview</h2>
            </div>
            <span className={`fireflyStudioState firefly-${screenStatus}`}>
              {screenStatus === 'live' ? 'Live' : screenStatus === 'error' ? 'Attention' : 'Waiting'}
            </span>
          </div>
          <p className='fireflyStudioCopy'>Share your screen when you want Firefly to inspect your code, product flow, or debugging steps.</p>
          <div className='fireflyScreenPreview fireflyStudioFrame'>
            <video ref={screenPreviewRef} autoPlay muted playsInline />
            <canvas ref={screenCanvasRef} className='fireflyHiddenCanvas' />
            {screenStatus !== 'live' ? (
              <div className='fireflyScreenEmpty'>
                <FontAwesomeIcon icon={faDisplay} />
                <strong>Screen sharing is off</strong>
                <p>Bring your workspace here whenever you want live visual help.</p>
              </div>
            ) : null}
          </div>
          <div className='fireflyStudioFooter'>
            <span>Best for code reviews, bug tracing, and app walkthroughs.</span>
          </div>
        </div>

        <div className='fireflyPanel fireflyCameraPanel fireflyStudioPanel'>
          <div className='fireflyStudioHeader'>
            <div className='fireflyStudioTitle'>
              <span className='fireflyPanelTag'>Camera View</span>
              <h2>Camera Preview</h2>
            </div>
            <span className={`fireflyStudioState firefly-${cameraStatus}`}>
              {cameraStatus === 'live' ? 'Live' : cameraStatus === 'error' ? 'Attention' : 'Waiting'}
            </span>
          </div>
          <p className='fireflyStudioCopy'>Turn on the camera for quick visual explanations, object checks, or off-screen teaching moments.</p>
          <div className='fireflyCameraPreview fireflyScreenPreview fireflyStudioFrame'>
            <video ref={cameraPreviewRef} autoPlay muted playsInline />
            <canvas ref={cameraCanvasRef} className='fireflyHiddenCanvas' />
            {cameraStatus !== 'live' ? (
              <div className='fireflyScreenEmpty'>
                <FontAwesomeIcon icon={faCamera} />
                <strong>Camera is off</strong>
                <p>Use it when you want Firefly to look at something beyond the screen.</p>
              </div>
            ) : null}
          </div>
          <div className='fireflyStudioFooter'>
            <span>Best for sketches on paper, devices, and real-world examples.</span>
          </div>
        </div>
      </section>

      <section className='fireflyTutorGrid fireflyTutorGridSingle'>
        <div className='fireflyBoardPanel'>
          <div className='fireflyAskPanel'>
            <div className='fireflyAskHeader'>
              <div className='fireflyAskTitle'>
                <span className='fireflyPanelTag'>Ask Firefly</span>
                <p>Ask for explanations, code, visual reasoning, or a board-ready output.</p>
              </div>
              <span className='fireflyAskHint'>
                {voiceStatus === 'live' ? 'Voice is live' : 'Chat mode'}
              </span>
            </div>

            <div className='fireflyComposer fireflyComposerBoard'>
              <textarea
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                placeholder='Ask Firefly to teach, debug, explain, or generate something for the board.'
              />
              <div className='fireflyComposerActions'>
                <button
                  type='button'
                  className='fireflySecondaryBtn'
                  onClick={analyzeCurrentVisual}
                  disabled={isAnalyzingVisual || isSending}
                >
                  <FontAwesomeIcon icon={activeVisualSource === 'camera' ? faCamera : faDisplay} />
                  {isAnalyzingVisual ? 'Inspecting...' : 'Analyze Active View'}
                </button>
                <button
                  type='button'
                  className='fireflyGhostBtn'
                  onClick={() =>
                    sendTutorRequest({
                      prompt: chatInput,
                      includeVisual: includeVisualInNextTurn,
                      toRealtime: voiceStatus === 'live',
                      visualSource: activeVisualSource,
                    })
                  }
                  disabled={isSending || isGeneratingAnalogy}
                >
                  <FontAwesomeIcon icon={voiceStatus === 'live' ? faPlug : faPaperPlane} />
                  {isGeneratingAnalogy ? 'Preparing Board...' : voiceStatus === 'live' ? 'Send To Voice' : isSending ? 'Thinking...' : 'Ask Firefly'}
                </button>
              </div>
            </div>
          </div>

          {isTranscriptVisible ? (
            <div className='fireflyTranscriptPanel'>
              <div className='fireflyPanelHeader'>
                <div>
                  <span className='fireflyPanelTag'>Transcript</span>
                  <h2>Conversation history</h2>
                  <p>Use this when you want to review the dialogue behind the lesson board.</p>
                </div>
              </div>

              <div className='fireflyMessages' ref={messagesContainerRef}>
                {messages.map((message) => (
                  <article key={message.id} className={`fireflyMessage fireflyMessage-${message.role}`}>
                    <div className='fireflyMessageMeta'>
                      <strong>{roleLabel[message.role] || 'Firefly'}</strong>
                      <span>{message.source}</span>
                    </div>
                    <div className='fireflyMessageBody'>{renderMessageBlocks(message.content)}</div>
                  </article>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  )
}

export default FireflyComp
